/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { NotificationService, SharedQueries } from '@core';
import { AccountUnitOfWork, DbQueryService } from '@data';
import { environment } from '@env';
import { firstValueFrom } from 'rxjs';
import {
  Account,
  AccountAddress,
  AccountAdmin,
  AccountAdminGroup,
  AdjustmentResult,
  CancellableDetailDTO,
  AccountAdminGroupMap,
  AccountBudget,
  AccountImage,
  AccountIssuance,
  AccountIssuanceUserGroupMap,
  AccountIssuanceUserLog,
  AccountProcurementCard,
  AccountProcurementCardLog,
  AccountPurchaseOrder,
  AccountPurchaseOrderLog,
  AccountUser,
  AccountUserAllowanceLog,
  ActiveStatusEnum,
  CollectionScopeEnum,
  Addon,
  AddonImage,
  ApprovalTree,
  ApprovalTreeAdminGroup,
  ApprovalTreeUserGroup,
  BudgetProductTypeTag,
  BudgetProductTypeTagMap,
  CancellationRequest,
  CancellationRequestDetail,
  CancellationRequestStatusEnum,
  Feature,
  InAppNotification,
  InvoiceHeaderDTO,
  IssuanceCoverageResult,
  TagConsolidationLegalityDto,
  TagConsolidationRequestDto,
  InvoiceQueryResultDTO,
  InvoiceSummaryDto,
  JobOrder,
  JobOrderBudgetLog,
  JobOrderDetail,
  JobOrderDetailAddon,
  JobOrderHistDetail,
  JobOrderHistDetailAddon,
  JobOrderHistDetailFeature,
  JobOrderHistDetailReceipt,
  JobOrderNote,
  JobOrderOveragePayment,
  JobOrderStatusEnum,
  JobOrderSummaryDto,
  UNPLACED_STATUSES,
  Kit,
  KitDetail,
  KitTag,
  NarrativeUserGroupDto,
  NotificationEventAccountMap,
  NotificationSubmission,
  NotificationTemplate,
  PricedProductType,
  ProductTypeConfig,
  ProductTypeConfigProduct,
  ProductTypeImage,
  Program,
  ProgramAccountIssuanceMap,
  ProgramAllowance,
  ProgramAllowanceUserGroupMap,
  ProgramAllowanceUserLog,
  ProgramBudget,
  ProgramIssuance,
  ProgramIssuanceUserGroupMap,
  ProgramIssuanceUserLog,
  ProgramKit,
  ProgramKitIssuance,
  ProgramKitTagMap,
  ProgramProductCategoryTag,
  ProgramProductTypeConfig,
  ProgramProductTypeTag,
  ProgramProductTypeTagMap,
  ProgramPurchaseOrderType,
  ProgramRapidTemplate,
  ProgramSupplier,
  ProgramTypeEnum,
  ProgramUserGroup,
  ProgramUserGroupBudget,
  ProgramUserGroupExclusion,
  ProgramUserGroupMap,
  ProgramView,
  ProximityAreaEnum,
  ProximityRight,
  ProximityRole,
  ProximityRoleRightMap,
  ProximityUserNote,
  ReturnPolicy,
  ReturnReason,
  ReturnRequest,
  ReturnRequestDetail,
  ReturnRequestStatusEnum,
  ShippingUserGroup,
  ShippingUserGroupAddressMap,
  ShippingUserGroupMap,
  Supplier,
  SupplierAccount,
  SupplierSubmission,
  Guideline,
} from '@models';
import { UtilFns, mapObject } from '@utils';
import { DataType, EntityQuery, MetadataStore } from 'breeze-client';
import * as _ from 'lodash';
import { JoHistSummary, JoHistTotalsBySupplier } from './account-interfaces';

/**
 * Summary of a fan-out fetch to one or more supplier-dependent endpoints across a set
 * of JobOrders. Callers inspect `ok` to decide whether to surface a banner/toast; the
 * pre-composed `message` gives a consistent user-facing phrasing across the app.
 *
 * As the multi-supplier network grows, this shape will expand to carry per-supplier
 * attribution (e.g. `failedSuppliers: string[]`) without breaking call sites.
 */
export interface SupplierFetchResult {
  ok: boolean;
  totalCount: number;
  failedCount: number;
  message?: string;
}

/**
 * Camel-cased shape of an AccountUserMatch returned by /Account/FindAccountUserForAdd.
 * The server emits PascalCase; we normalize here so consumers can use camelCase bindings.
 */
export interface AccountUserMatch {
  accountUserId: string;
  proximityUserId: string;
  fullName: string;
  username: string;
  email: string;
  currentPugIds: string[];
}

@Injectable({ providedIn: 'root' })
export class AccountDbQueryService extends DbQueryService {
  constructor(
    public override uow: AccountUnitOfWork,
    private http: HttpClient,
    private notificationService: NotificationService,
  ) {
    super(uow);
    // Make sure that you don't strand a mapping type by deleting either end but leaving it intact.  And don't add lookup types.
    this.registeredEntityTypes = [
      Account,
      AccountUser,
      AccountAdmin,
      AccountBudget,
      AccountPurchaseOrder,
      AccountIssuance,
      AccountIssuanceUserGroupMap,
      AccountIssuanceUserLog,

      ApprovalTreeUserGroup,
      ApprovalTreeAdminGroup,

      BudgetProductTypeTag,
      BudgetProductTypeTagMap,

      Kit,
      KitDetail,

      Program,
      ProgramAccountIssuanceMap,
      ProgramSupplier,
      ProgramProductTypeConfig,
      ProductTypeConfig,
      PricedProductType,
      ProgramBudget,
      ProgramUserGroup,
      ProgramUserGroupMap,
      ProgramUserGroupExclusion,
      ProgramUserGroupBudget,
      ProgramProductCategoryTag,
      ProgramProductTypeTag,
      ProgramProductTypeTagMap,
      ProgramIssuance,
      ProgramIssuanceUserLog,
      ProgramIssuanceUserGroupMap,
      ProgramAllowance,
      ProgramAllowanceUserLog,
      ProgramAllowanceUserGroupMap,
      ProgramKit,
      KitTag,
      ProgramKitTagMap,
      JobOrder,
      JobOrderDetail,
      JobOrderDetailAddon,
      JobOrderHistDetail,
      JobOrderHistDetailFeature,
      JobOrderHistDetailAddon,
      JobOrderHistDetailReceipt,
      JobOrderOveragePayment,
      JobOrderNote,
      JobOrderBudgetLog,

      ShippingUserGroup,
      ShippingUserGroupMap,
      SupplierAccount,
      SupplierSubmission,
    ];
  }

  //#region Supplier related queries

  async getSuppliers() {
    const r = await this.uow.createQuery(Supplier).execute();
    return r;
  }

  // have not tested yet
  // async getEntityById(entityTypeName: string, entityId: string) {
  //   const et = this.metadataStore.getAsEntityType(entityTypeName);
  //   const ctor = et.getCtor() as new () => Entity;
  //   const r = await this.uow.createQuery(ctor).where( { id: entityId}).execute();
  //   UtilFns.assertArrayOfOne(r);
  //   return r[0];
  // }

  async getSupplierById(supplierId: string) {
    const r = await this.uow.createQuery(Supplier).where({ id: supplierId }).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getSupplierAccountById(supplierAccountId: string) {
    const r = await this.uow.createQuery(SupplierAccount).where({ id: supplierAccountId }).expand(['account']).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getSupplierAccount(supplierId: string, accountId: string) {
    const r = await this.uow
      .createQuery(SupplierAccount)
      .where({
        supplierId: supplierId,
        accountId: accountId,
      })
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getSupplierSubmissions(afterDate: Date) {
    const r = await this.uow
      .createQuery(SupplierSubmission)
      .where({ submissionTs: { ge: afterDate } })
      .expand('supplier')
      .execute();

    return r;
  }

  async getEntityDescription(tableName: string, entityId: string) {
    if (tableName == 'JobOrders') {
      const jo = await this.getJobOrderById(entityId);
      return jo.account.name + ':  Job Order: ' + jo.displayId;
    } else if (tableName == 'SupplierAccount') {
      const sa = await this.getSupplierAccountById(entityId);
      return sa.account.name;
    } else if (tableName == 'CancellationRequests') {
      const cr = await this.getCancellationRequestById(entityId);
      return cr?.jobOrder.account.name + ':  Job Order: ' + cr?.jobOrder.displayId;
    } else if (tableName == 'ReturnRequests') {
      const rr = await this.getReturnRequestById(entityId);
      return rr?.jobOrder.account.name + ':  Job Order: ' + rr?.jobOrder.displayId;
    } else {
      return 'Unknown';
    }
  }

  getAddons(supplierId: string) {
    const r = this.uow.createQuery(Addon).where({ supplierId }).orderBy('nameAndLocation').execute();
    return r;
  }

  //#endregion

  //#region Account

  async getAccountForValidationById(accountId: string) {
    const r = await this.uow
      .createQuery(Account)
      .where({ id: accountId })
      .expand([
        'primaryAccountAdmin.proximityUser',
        'accountAdmins.proximityUser.proximityRole',
        'accountAdminGroups',
        'accountAdminGroups.accountAdminGroupMaps',
        'programs',
        'approvalTrees.approvalTreeUserGroups.programUserGroup',
        'approvalTrees.approvalTreeUserGroups.programUserGroup.programUserGroupMaps.accountUser.proximityUser',
        'approvalTrees.approvalTreeUserGroups.accountPurchaseOrder',
        'approvalTrees.approvalTreeUserGroups.accountProcurementCard',
        'shippingUserGroups.shippingUserGroupMaps',
        'approvalTrees.approvalTreeAdminGroups.accountAdminGroup.accountAdminGroupMaps',
      ])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getAccountById(accountId: string) {
    const r = await this.uow.createQuery(Account).where({ id: accountId }).expand(['supplierAccounts.assignedSupplierAdmin.proximityUser', 'manifests']).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getAccountAndApprovalTreesById(accountId: string) {
    const r = await this.uow
      .createQuery(Account)
      .where({ id: accountId })
      .expand(['approvalTrees.approvalTreeAdminGroups', 'approvalTrees.approvalTreeUserGroups', 'supplierAccounts'])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getAccountsByName(name: string) {
    const r = await this.uow
      .createQuery(Account)
      .where({ name: { startsWith: name } })
      .execute();
    return r;
  }

  async getAccountWithIssuancesAndSupplierAccounts(accountId: string) {
    const r = await this.uow
      .createQuery(Account)
      .where({ id: accountId })
      .expand(['accountIssuances.accountIssuanceUserGroupMaps', 'accountIssuances.programProductTypeTag', 'supplierAccounts'])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getAccountByIdWithAdmins(accountId: string) {
    const r = await this.uow
      .createQuery(Account)
      .where({ id: accountId })
      .expand(['primaryAccountAdmin', 'accountAdmins.proximityUser', 'accountAdminGroups.accountAdminGroupMaps.accountAdmin.proximityUser'])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getAccountByIdForDelete(accountId: string) {
    const r = await this.uow.createQuery<Account>(Account, 'AccountByIdForDelete').withParameters({ accountId }).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  //#endregion

  //#region Account Admin

  async getAccountAdminById(accountAdminId: string) {
    const r = await this.uow.createQuery(AccountAdmin).where({ id: accountAdminId }).expand(['proximityUser.accountUsers']).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getAccountAdminsByAccountId(accountId: string) {
    return this.uow
      .createQuery(AccountAdmin)
      .where({ accountId: accountId })
      .expand(['account', 'proximityUser', 'proximityUser.proximityRole'])
      .execute();
  }

  async getAccountAdminsByAdminGroup(accountAdminGroupId: string) {
    return this.uow
      .createQuery(AccountAdmin)
      .where({
        accountAdminGroupMaps: {
          any: { accountAdminGroupId: accountAdminGroupId },
        },
      })
      .expand(['proximityUser'])
      .execute();
  }

  /** Return both account-specific and global roles */
  async getAccountRolesByAccountId(accountId: string) {
    return this.uow
      .createQuery(ProximityRole)
      .where({ areaId: ProximityAreaEnum.Accounts })
      .where({ or: [{ accountId: null }, { accountId }] })
      .expand(['proximityRoleRightMaps'])
      .execute();
  }

  async getAccountUserRoles(accountId: string) {
    return this.uow
      .createQuery(ProximityRole)
      .where({ areaId: ProximityAreaEnum.AccountUsers })
      .where({ or: [{ accountId: null }, { accountId }] })
      .expand(['proximityRoleRightMaps'])
      .execute();
  }

  async getAllRoles() {
    return this.uow.createQuery(ProximityRole).expand(['proximityRoleRightMaps']).execute();
  }

  //#endregion

  //#region AccountAdminGroup related

  async getAccountAdminGroups(accountId: string) {
    return this.uow
      .createQuery(AccountAdminGroup)
      .where({ accountId: accountId })
      .expand(['accountAdminGroupMaps.accountAdmin.proximityUser'])
      .execute();
  }

  // WIP
  async getAccountAdminGroupsByAdminId(accountAdminId: string) {
    return this.uow
      .createQuery(AccountAdminGroup)
      .where({
        accountAdminGroupMaps: {
          any: { accountAdminId: accountAdminId },
        },
      })

      .execute();
  }

  async getAccountAdminGroupAdmins(accountAdminGroupId: string, useCached = true) {
    const q = this.uow.createQuery(AccountAdminGroupMap).where({ accountAdminGroupId }).expand(['accountAdmin.proximityUser']);
    const r = useCached ? await q.execute() : await q.execute();
    return r.map((x) => x.accountAdmin);
  }

  //#endregion

  //#region AccountUser

  async getAccountUsers(accountId: string, useCached = true) {
    const q = this.uow.createQuery(AccountUser).where({ accountId: accountId }).expand(['account', 'proximityUser']);
    return useCached ? q.execute() : q.execute();
  }

  /**
   * Load the Account Users that an import matched, with everything an update touches, so the
   * entities are in the EntityManager before the import mutates them. One query, not one per
   * row — the import can match hundreds of people at once.
   */
  async getAccountUsersForImportUpdate(accountUserIds: string[]) {
    if (accountUserIds.length === 0) return [] as AccountUser[];
    return await this.uow
      .createQuery(AccountUser)
      .where({ id: { in: accountUserIds } })
      .expand(['proximityUser', 'shippingUserGroupMaps.shippingUserGroup', 'programUserGroupMaps.programUserGroup'])
      .execute();
  }

  async getAccountUserById(accountUserId: string, simple = false) {
    let q = this.uow.createQuery(AccountUser).where({ id: accountUserId });
    if (simple) {
      q = q.expand(['account']);
    } else {
      q = q.expand([
        'account',
        'billingAccountAddress',
        'shippingAccountAddress',
        'proximityUser.accountAdmins',
        'programUserGroupMaps.programUserGroup',
        'programUserGroupExclusions',
        'shippingUserGroupMaps.shippingUserGroup',
      ]);
    }
    const r = await q.execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getAccountUsersForAccountAdminId(accountAdminId: string, includeHierarchy: boolean) {
    const r = await this.uow
      .createQuery<AccountUser>(AccountUser, 'GetAccountUsersForAccountAdmin')
      .withParameters({
        accountAdminId,
        includeHierarchy,
      })
      .execute();
    return r;
  }

  createAccountUsersForAccountAdminIdQuery(accountAdminId: string, includeHierarchy: boolean) {
    // Use the *Query (IQueryable) endpoint, not the non-suffixed one that materializes via
    // .ToList() before any filtering. With the materialized version, ag-Grid server-side
    // filters get applied in C# memory where string.Contains is case-sensitive — even though
    // SQL Server's CI collation would have handled the same filter correctly if pushed down.
    // 'proximityUser.activeStatus' matches createAllAccountUsersQuery. Without it the Status
    // column (which reads proximityUser.activeStatus.name) is blank for every restricted admin,
    // because this is the only one of the three AdminScopeService branches that omitted it.
    const q = this.uow.createQuery<AccountUser>(AccountUser, 'GetAccountUsersForAccountAdminQuery').withParameters({
      accountAdminId,
      includeHierarchy,
    }).expand(['account', 'proximityUser.activeStatus', 'programUserGroupMaps.programUserGroup']);
    return q;
  }

  async getAccountUsersForProgramUserGroup(programUserGroupId: string) {
    const r = await this.getProgramUserGroupMaps(programUserGroupId);
    return r.map((r) => r.accountUser);
  }

  async getAccountUsersForShippingUserGroup(shippingUserGroupId: string) {
    const r = await this.getShippingUserGroupMaps(shippingUserGroupId);
    return r.map((r) => r.accountUser);
  }

  /**
   * Account Users grid query.
   * programUserGroupIds / includeUsersWithNoPug drive the grid-wide PUG filter. The two are OR-ed
   * together so that picking some PUGs *and* "No PUGs Assigned" returns the union, then AND-ed onto
   * the account/status predicate. Breeze cannot reach a collection with a dotted path -- collections
   * need an explicit any / not-any (same shape as createPricedProductTypesQuery).
   */
  createAccountUsersQuery(
    accountId: string,
    showInactive: boolean = false,
    programUserGroupIds: string[] = [],
    includeUsersWithNoPug: boolean = false,
  ) {
    let q = this.uow.createQuery(AccountUser).expand(['account', 'proximityUser', 'programUserGroupMaps.programUserGroup']);
    const predicate: Record<string, unknown> = { accountId: accountId };
    if (!showInactive) {
      predicate['proximityUser.activeStatusId'] = { ne: ActiveStatusEnum.Inactive };
    }
    q = q.where(predicate);

    const pugClauses: object[] = [];
    if (programUserGroupIds.length > 0) {
      pugClauses.push({ programUserGroupMaps: { any: { programUserGroupId: { in: programUserGroupIds } } } });
    }
    if (includeUsersWithNoPug) {
      // ProgramUserGroupId is non-nullable, so any(... ne null) means "has at least one map".
      pugClauses.push({ not: { programUserGroupMaps: { any: { programUserGroupId: { ne: null } } } } });
    }
    // Breeze AND-s successive .where() calls, so these narrow the predicate rather than replace it.
    if (pugClauses.length === 1) {
      q = q.where(pugClauses[0]);
    } else if (pugClauses.length > 1) {
      q = q.where({ or: pugClauses });
    }
    return q;
  }

  /**
   * PUGs offered in the Account Users grid filter. Excludes only Inactive -- Hold and Phaseout PUGs
   * still have users attached that admins expect to be able to find.
   */
  async getSelectableProgramUserGroups(accountId: string) {
    return this.uow
      .createQuery(ProgramUserGroup)
      .where({ accountId: accountId, activeStatusId: { ne: ActiveStatusEnum.Inactive } })
      .orderBy('name')
      .execute();
  }

  /** Server-side query for all account users across all accounts (global admin view). */
  createAllAccountUsersQuery() {
    return this.uow
      .createQuery(AccountUser)
      .expand(['account', 'proximityUser.activeStatus', 'programUserGroupMaps.programUserGroup']);
  }

  /**
   * Get program access timeline data for a user.
   * Returns all programs the user has access to, along with their access periods.
   */
  async getUserProgramAccessTimeline(accountUserId: string): Promise<
    {
      programId: string;
      programName: string;
      programUserGroupName: string;
      joinDate: Date;
      endDate: Date | null;
      isExcluded: boolean;
      isExpired: boolean;
      isFuture: boolean;
      programStatus: string;
    }[]
  > {
    // Get the user's program user group mappings with expanded data
    const userMaps = await this.uow
      .createQuery(ProgramUserGroupMap)
      .where({ accountUserId })
      .expand(['programUserGroup', 'accountUser'])
      .execute();

    if (userMaps.length === 0) {
      return [];
    }

    // Get all programs that use these program user groups (via ApprovalTree)
    const programUserGroupIds = [...new Set(userMaps.map((m) => m.programUserGroupId))];

    const programs = await this.uow
      .createQuery(Program)
      .where({
        'approvalTree.approvalTreeUserGroups': {
          any: { programUserGroupId: { in: programUserGroupIds }, activeStatusId: ActiveStatusEnum.Active },
        },
      })
      .expand(['approvalTree.approvalTreeUserGroups.programUserGroup', 'activeStatus'])
      .execute();

    // Get user's exclusions
    const exclusions = await this.uow.createQuery(ProgramUserGroupExclusion).where({ accountUserId }).execute();

    const exclusionKeys = new Set(exclusions.map((e) => `${e.programId}:${e.programUserGroupId}`));

    // Get user's exit date (if any)
    const accountUser = userMaps[0]?.accountUser;
    const userExitDate = accountUser?.exitDate ? new Date(accountUser.exitDate) : null;

    // Build result
    const result: {
      programId: string;
      programName: string;
      programUserGroupName: string;
      joinDate: Date;
      endDate: Date | null;
      isExcluded: boolean;
      isExpired: boolean;
      isFuture: boolean;
      programStatus: string;
    }[] = [];

    const today = new Date();

    for (const program of programs) {
      // Find which user groups connect this user to this program
      const programUserGroups =
        program.approvalTree?.approvalTreeUserGroups
          ?.filter((atug) => programUserGroupIds.includes(atug.programUserGroupId) && atug.activeStatusId === ActiveStatusEnum.Active)
          ?.map((atug) => atug.programUserGroup) || [];

      for (const pug of programUserGroups) {
        // Find the user's join date for this program user group
        const userMap = userMaps.find((m) => m.programUserGroupId === pug.id);
        if (!userMap) continue;

        // Use the later of: user's join date to the group, or program's start date
        const userJoinDate = userMap.joinDate ? new Date(userMap.joinDate) : new Date();
        const programStartDate = program.startDate ? new Date(program.startDate) : null;
        const joinDate = programStartDate && programStartDate > userJoinDate ? programStartDate : userJoinDate;

        // Calculate end date
        let endDate: Date | null = null;
        if (userExitDate && userExitDate < today) {
          endDate = userExitDate;
        } else if (program.endDate) {
          endDate = new Date(program.endDate);
        }

        // Check if excluded
        const isExcluded = exclusionKeys.has(`${program.id}:${pug.id}`);

        // Check if expired
        const isExpired = (endDate !== null && endDate < today) || program.activeStatusId === ActiveStatusEnum.Inactive;

        // Check if future (hasn't started yet)
        const isFuture = joinDate > today;

        result.push({
          programId: program.id,
          programName: program.name,
          programUserGroupName: pug.name,
          joinDate,
          endDate,
          isExcluded,
          isExpired,
          isFuture,
          programStatus: program.activeStatus?.name || 'Unknown',
        });
      }
    }

    // Sort by program name, then join date
    result.sort((a, b) => {
      const nameCompare = a.programName.localeCompare(b.programName);
      if (nameCompare !== 0) return nameCompare;
      return a.joinDate.getTime() - b.joinDate.getTime();
    });

    return result;
  }

  //#endregion

  //#region ApprovalTrees related

  async getApprovalTrees(accountId: string) {
    return this.uow
      .createQuery(ApprovalTree)
      .where({ accountId: accountId })
      .expand([
        'approvalTreeAdminGroups.accountAdminGroup',
        'approvalTreeAdminGroups.approvalTreeUserGroups.programUserGroup',
        'approvalTreeAdminGroups.approvalTreeAdminGroupLogs',
      ])
      .execute();
  }

  async getApprovalTreesByAccountAdmin(accountAdminId: string) {
    const r = await this.uow
      .createQuery(ApprovalTreeAdminGroup)
      .where({
        'accountAdminGroup.accountAdminGroupMaps': {
          any: { accountAdminId: accountAdminId },
        },
      })
      .expand([
        'approvalTree.account',
        'approvalTree.approvalTreeAdminGroups.approvalTreeUserGroups.programUserGroup',
        // 'approvalTreeUserGroups.programUserGroup', // this is only the approvalTreeUserGroups for this adminGroup - and not for the entire tree
      ])
      .execute();

    return r.map((x) => x.approvalTree);
  }

  async getApprovalTreeAdminGroupForApprovalTreeProgramAndUser(approvalTreeId: string, programId: string, accountUserId: string) {
    const r = await this.uow
      .createQuery(ApprovalTreeAdminGroup, 'ApprovalTreeAdminGroupForApprovalTreeProgramAndUser')
      .withParameters({ approvalTreeId, programId, accountUserId })
      .execute();
    UtilFns.assertArrayZeroOrOne(r, 'should only find zero or one admin group for this user in this tree');
    return _.first(r);
  }

  //#endregion

  //#region AccountPurchaseOrder Queries

  async getAccountPurchaseOrderById(accountPurchaseOrderId: string) {
    const r = await this.uow
      .createQuery(AccountPurchaseOrder)
      .where({ id: accountPurchaseOrderId })
      .expand(['accountPurchaseOrderLogs'])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getAccountPurchaseOrders(accountId: string) {
    return this.uow
      .createQuery(AccountPurchaseOrder)
      .where({ accountId: accountId })
      .expand(['accountPurchaseOrderLogs', 'parentAccountPurchaseOrder', 'childAccountPurchaseOrders'])
      .execute();
  }

  // async getAccountPurchaseOrders(accountId: string) {
  //   return this.uow
  //     .createQuery(AccountPurchaseOrder)
  //     .where({ accountId: accountId })
  //     .expand(['parentAccountPurchaseOrder', 'childAccountPurchaseOrders'])
  //     .execute();
  // }

  async getAccountChildrenPurchaseOrdersByParentId(accountId: string, parentPurchaseOrderId: string) {
    return this.uow
      .createQuery(AccountPurchaseOrder)
      .where({ accountId: accountId, parentAccountPurchaseOrderId: parentPurchaseOrderId })
      .expand(['childAccountPurchaseOrders'])
      .execute();
  }

  async getAccountPurchaseOrderLogs(accountPurchaseOrderId: string) {
    return this.uow.createQuery(AccountPurchaseOrderLog).where({ accountPurchaseOrderId: accountPurchaseOrderId }).execute();
  }

  async getAccountPurchaseOrderLogsWithDetails(accountPurchaseOrderId: string) {
    return this.uow
      .createQuery(AccountPurchaseOrderLog)
      .where({ accountPurchaseOrderId: accountPurchaseOrderId })
      .expand(['jobOrder.accountUser.proximityUser'])
      .execute();
  }

  //#endregion

  //#region AccountProcurementCards

  async getAccountProcurementCards(accountId: string, activeOnly: boolean = true) {
    let q = this.uow.createQuery(AccountProcurementCard).where({ accountId: accountId });
    if (activeOnly) {
      q = q.where({ active: true });
    }
    return q.expand(['accountProcurementCardLogs']).execute();
  }

  async getAccountProcurementCardLogs(accountProcurementCardId: string) {
    return this.uow.createQuery(AccountProcurementCardLog).where({ accountProcurementCardId }).execute();
  }

  //#endregion

  //#region Kits

  async createKitsByAccountIdQuery(accountId: string) {
    const q = this.uow
      .createQuery(Kit)
      .where({ 'supplierAccount.accountId': accountId })
      .expand([
        'supplierAccount.account',
        'activeStatus',
        'kitDetails.productTypeConfigProduct.productTypeConfig.productTypeConfigAddons.pricedAddon.addon',
        'kitDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.pricedProductTypeFeatureChoices',
        'kitDetails.productTypeConfigProduct.product.productType.manufacturer',
        'kitDetails.productTypeConfigProduct.product.productFeatureChoices.featureChoice.feature.featureChoices',
      ]);
    return q;
  }

  async getProgramKitsByProgramId(programId: string) {
    const r = await this.uow
      .createQuery(ProgramKit)
      .where({ programId: programId })
      .expand([
        'programKitTagMaps.kitTag',
        'kit.activeStatus',
        // supplier nav loaded so order entry can block kits whose supplier / supplier-account is not Active
        'kit.supplierAccount.supplier',
        'kit.kitDetails.productTypeConfigProduct.productTypeConfig.productTypeConfigAddons.pricedAddon.addon',
        'kit.kitDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.pricedProductTypeFeatureChoices',
        'kit.kitDetails.productTypeConfigProduct.product.productType.manufacturer',
        'kit.kitDetails.productTypeConfigProduct.product.productType.supplier',
        'kit.kitDetails.productTypeConfigProduct.product.productFeatureChoices.featureChoice.feature.featureChoices',
      ])
      .execute();
    return r;
  }

  async getProgramKitById(programKitId: string) {
    const r = await this.uow
      .createQuery(ProgramKit)
      .where({ id: programKitId })
      .expand([
        'programKitTagMaps.kitTag',
        'kit.activeStatus',
        // supplier nav loaded so order entry can block kits whose supplier / supplier-account is not Active
        'kit.supplierAccount.supplier',
        'kit.kitDetails.productTypeConfigProduct.productTypeConfig.productTypeConfigAddons.pricedAddon.addon',
        'kit.kitDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.pricedProductTypeFeatureChoices',
        'kit.kitDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.productType.productTypeImages.supplierImage',
        'kit.kitDetails.productTypeConfigProduct.productTypeConfig.returnPolicy',
        'kit.kitDetails.productTypeConfigProduct.product.productType.manufacturer',
        'kit.kitDetails.productTypeConfigProduct.product.productType.supplier',
        'kit.kitDetails.productTypeConfigProduct.product.productFeatureChoices.featureChoice.feature.featureChoices',
      ])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  createKitsQuery(accountId: string, supplierId: string | null, shouldIncludePricing: boolean) {
    return SharedQueries.createProductTypeConfigsQuery(this.uow, accountId, supplierId, shouldIncludePricing);
  }

  //#endregion

  //#region Shipping Related

  async getShippingUserGroups(accountId: string, activeOnly: boolean = false) {
    if (activeOnly) {
      return this.uow
        .createQuery(ShippingUserGroup)
        .where({ accountId: accountId, activeStatusId: ActiveStatusEnum.Active })
        .expand(['account', 'shippingUserGroupAddressMaps.shippingAccountAddress', 'shippingUserGroupMaps.accountUser.proximityUser'])
        .orderBy('name')
        .execute();
    } else {
      return this.uow
        .createQuery(ShippingUserGroup)
        .where({ accountId: accountId })
        .expand(['account', 'shippingUserGroupAddressMaps.shippingAccountAddress', 'shippingUserGroupMaps.accountUser.proximityUser'])
        .orderBy('name')
        .execute();
    }
  }

  async getShippingUserGroupsByShippingAddress(addId: string) {
    const r = await this.uow
      .createQuery(ShippingUserGroupAddressMap)
      .where({ shippingAccountAddressId: addId })
      .expand(['shippingUserGroup'])
      .execute();
    return r.map((x) => x.shippingUserGroup);
  }

  async getJobOrdersByShippingAddressCount(addId: string) {
    const q = this.uow.createQuery(null, 'GetJobOrdersByShippingAddressCount').withParameters({ addId: addId });
    const r = await q.execute();
    return _.first(r) as unknown as number;
  }

  async getJobOrdersByBillingAddressCount(addId: string) {
    const q = this.uow.createQuery(null, 'GetJobOrdersByBillingAddressCount').withParameters({ addId: addId });
    const r = await q.execute();
    return _.first(r) as unknown as number;
  }

  async getShippingUserGroupsForUser(accountUserId: string) {
    const r = await this.uow
      .createQuery(ShippingUserGroupMap)
      .where({ accountUserId: accountUserId })
      .expand(['shippingUserGroup'])
      .execute();

    return r.map((x) => x.shippingUserGroup);
  }

  async getShippingAccountAddresses(accountId: string, accountUserId?: string, canShipHome?: boolean) {
    let q = this.uow
      .createQuery(AccountAddress)
      .where({
        accountId: accountId,
        isShippingAddress: true,
        isPersonalAddress: false,
      })
      .expand(['account', 'shippingUserGroupAddressMaps.shippingUserGroup.shippingUserGroupMaps.accountUser.proximityUser']);
    if (accountUserId) {
      q = q.where({
        shippingUserGroupAddressMaps: {
          any: {
            'shippingUserGroup.shippingUserGroupMaps': {
              any: { accountUserId: accountUserId },
            },
          },
        },
      });
    }
    const addresses = await q.execute();

    if (accountUserId && canShipHome) {
      const r2 = await this.createQuery(AccountUser).where({ id: accountUserId }).expand(['shippingAccountAddress']).execute();
      const homeAddress = r2[0].shippingAccountAddress;
      if (homeAddress) {
        addresses.push(homeAddress);
      }
    }
    return addresses;
  }

  //#endregion

  //#region Tags

  async getProgramProductCategoryTags(programId: string) {
    return this.uow
      .createQuery(ProgramProductCategoryTag)
      .where({ programId: programId })
      .expand(['programProductCategoryTagMaps.programProductCategoryTag'])
      .execute();
  }

  async getProgramProductTypeTagsForAccount(accountId: string) {
    return this.uow
      .createQuery(ProgramProductTypeTag)
      .where({
        accountId: accountId,
      })
      .expand([
        'programProductTypeTagMaps.pricedProductType.productType.manufacturer',
        'programProductTypeTagMaps.pricedProductType.productType.supplier',
        'programProductCategoryTagMaps.programProductCategoryTag',
      ])
      .execute();
  }

  async getBudgetProductTypeTags(accountId: string) {
    return this.uow
      .createQuery(BudgetProductTypeTag)
      .where({
        accountId: accountId,
      })
      .expand(['budgetProductTypeTagMaps'])
      .execute();
  }

  async getBudgetProductTypeTagMapsForTag(budgetProductTypeTagId: string) {
    return this.uow
      .createQuery(BudgetProductTypeTagMap)
      .where({ budgetProductTypeTagId: budgetProductTypeTagId })
      .expand(['pricedProductType.productType.manufacturer', 'pricedProductType.productType.supplier'])
      .execute();
  }

  async getProgramProductTypeTag(programProductTypeTagId: string) {
    return this.uow
      .createQuery(ProgramProductTypeTag)
      .where({ id: programProductTypeTagId })
      .expand([
        'programProductTypeTagMaps.pricedProductType.productType.manufacturer',
        'programProductTypeTagMaps.pricedProductType.productType.supplier',
        'programProductCategoryTagMaps.programProductCategoryTag.program',
      ])
      .execute();
  }

  async getProgramProductTypeTagMapsForTag(programProductTypeTagId: string) {
    return this.uow
      .createQuery(ProgramProductTypeTagMap)
      .where({ programProductTypeTagId: programProductTypeTagId })
      .expand(['pricedProductType.productType.manufacturer', 'pricedProductType.productType.supplier'])
      .execute();
  }

  async getPricedProductTypeById(pricedProductTypeId: string) {
    return this.uow
      .createQuery(PricedProductType)
      .where({ id: pricedProductTypeId })
      .expand([
        'productType.manufacturer',
        'productType.supplier',
        'programProductTypeTagMaps.programProductTypeTag',
        'productTypeConfigs.programProductTypeConfigs.program.programIssuances',
        'productTypeConfigs.programProductTypeConfigs.program.programProductTypeConfigs.productTypeConfig.pricedProductType',
        'account.accountIssuances',
        // JM 3/25/2026 — Additional expands needed for product-centric issuance detach check.
        // checkIfStrandedCore needs to determine issuance-only PUGs and which PUGs each issuance applies to.
        'productTypeConfigs.programProductTypeConfigs.program.approvalTree.approvalTreeUserGroups.programUserGroup',
        'productTypeConfigs.programProductTypeConfigs.program.programAllowances.programAllowanceUserGroupMaps',
        'productTypeConfigs.programProductTypeConfigs.program.programIssuances.programIssuanceUserGroupMaps',
        'productTypeConfigs.programProductTypeConfigs.program.programAccountIssuanceMaps.accountIssuance.accountIssuanceUserGroupMaps',
      ])
      .execute();
  }

  async getPricedProductTypesForTag(programProductTypeTagId: string) {
    return this.uow
      .createQuery(PricedProductType)
      .where({
        programProductTypeTagMaps: {
          any: { programProductTypeTagId: programProductTypeTagId },
        },
      })
      .expand(['productType.manufacturer', 'productType.supplier'])
      .execute();
  }

  async getPricedProductTypesForAccount(accountId: string) {
    return this.uow
      .createQuery(PricedProductType)
      .where({ accountId: accountId })
      .expand(['productType.manufacturer', 'productType.supplier', 'programProductTypeTagMaps.programProductTypeTag'])
      .execute();
  }

  async getKitTagsForAccount(accountId: string) {
    return this.uow
      .createQuery(KitTag)
      .where({
        accountId: accountId,
      })
      .execute();
  }

  async getProgramKitTagMapsForTag(programKitTagId: string) {
    return this.uow.createQuery(ProgramKitTagMap).where({ programKiteTagId: programKitTagId }).expand(['program']).execute();
  }

  //#endregion

  //#region PricedProductTypes

  createPricedProductTypesQuery(accountId: string, programProductTypeTagId: string) {
    return this.uow
      .createQuery(PricedProductType)
      .where({
        accountId: accountId,
        not: {
          programProductTypeTagMaps: {
            any: { programProductTypeTagId: programProductTypeTagId },
          },
        },
      })
      .expand([
        'programProductTypeTagMaps',
        'productType.productTypeFeatures',
        'productType.productTypeFeatures.feature',
        'productType.manufacturer',
        'productType.supplier',
      ]);
  }

  createPricedProductTypesForProgramQuery(programId: string) {
    return this.uow
      .createQuery(PricedProductType)
      .where({
        productTypeConfigs: {
          any: { programProductTypeConfigs: { any: { programId: programId } } },
        },
      })
      .expand(['productType.manufacturer', 'productType.supplier']);
  }

  createPricedProductTypesWithConfigByAccountQuery(accountId: string) {
    const q = this.uow
      .createQuery<PricedProductType>(PricedProductType, 'PricedProductTypesWithConfigByAccount')
      .withParameters({ accountId });
    return q;
  }

  //#endregion

  //#region Programs

  createProgramsQuery(accountId: string) {
    return this.uow.createQuery(Program).where({ accountId: accountId });
  }

  async getPrograms(accountId: string) {
    return await this.uow.createQuery(Program).where({ accountId: accountId }).execute();
  }

  /**
   * ProgramSuppliers belonging to Managed-Distribution programs in this account
   * whose program is Active or Hold and (endDate null OR endDate >= today).
   * Used by the Managed-Inventory Program Information grid to let the admin
   * connect any of those MD programs to draw from this Inventory Program.
   *
   * Caller filters further to just those whose SupplierAccount.AllowInventory === true
   * (that lookup uses the already-loaded account.supplierAccounts collection).
   */
  async getManagedProgramSuppliersForAccount(accountId: string) {
    const today = new Date();
    return this.uow
      .createQuery(ProgramSupplier)
      .where({
        'program.accountId': accountId,
        'program.programTypeId': ProgramTypeEnum.Managed,
        'program.activeStatusId': { in: [ActiveStatusEnum.Active, ActiveStatusEnum.Hold] },
        or: [{ 'program.endDate': null }, { 'program.endDate': { ge: today } }],
      })
      .expand(['program.activeStatus', 'supplier', 'inventoryProgram'])
      .execute();
  }

  async getProgramsWithSameName(programId: string, programName: string, accountId: string) {
    const r = await this.uow.createQuery(Program).where({ name: programName, accountId: accountId }).execute();
    return r.filter((p) => p.id !== programId);
  }

  async getProgramByIdSimple(programId: string) {
    const r = await this.uow.createQuery(Program).where({ id: programId }).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getProgramById(programId: string) {
    const r = await this.uow.createQuery<Program>(Program, 'ProgramById').withParameters({ programId }).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getProgramByIdFull(programId: string) {
    const r = await this.uow.createQuery<Program>(Program, 'ProgramByIdFull').withParameters({ programId }).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getProgramWithApprovalTree(programId: string) {
    const r = await this.uow
      .createQuery(Program)
      .where({ id: programId })
      .expand(['approvalTree.approvalTreeUserGroups.programUserGroup'])
      .execute();
    return r.length > 0 ? r[0] : null;
  }

  /**
   * Programs reachable from a set of ProgramUserGroups, through the shared ApprovalTree.
   *
   * ⚠️ DO NOT EXPAND THE MEMBERSHIP ROWS BY DEFAULT.
   *
   * Approval trees are SHARED across programs, and one PUG serves many programs, so
   * `approvalTree.approvalTreeUserGroups` fans out to every PUG on every tree these programs sit
   * on - not just the ones that were asked for. This expand used to end in
   * `.programUserGroup.programUserGroupMaps.accountUser`, which walked that fan-out all the way
   * down to the people: opening ONE account user downloaded the roster of every PUG on every tree
   * any of that user's programs touched. Measured on production 2026-10-12: one DOT-FMCSA tree
   * carries 25 PUGs and 2,275 membership rows, one Pennsylvania Turnpike tree carries 394 PUGs.
   * The response was 1,326 kB and 1.16 s for a single user's page.
   *
   * Nothing read it. `buildProgramAccessItems` uses `atug.programUserGroupId`, a scalar column on
   * the ApprovalTreeUserGroup row, and takes PUG names from the user's OWN membership rows
   * (`getProgramUserGroupMapsForUser`) rather than from the tree. The one caller that genuinely
   * iterates memberships - `createExclusionsForDupUsers` - reads only the `accountUserId` FK, so
   * even it never needed the AccountUser entity behind the map.
   *
   * @param skipAllowances       Omit the allowance and issuance detail. For callers that only
   *                             need program status and dates.
   * @param includeUserGroupMaps Expand the membership rows on each PUG. Opt in, and only when the
   *                             caller walks the memberships of OTHER users - not just this one.
   */
  async getProgramsForProgramUserGroups(
    programUserGroupIds: string[],
    skipAllowances = false,
    includeUserGroupMaps = false,
  ) {
    let q = this.uow.createQuery(Program).where({
      'approvalTree.approvalTreeUserGroups': {
        any: { programUserGroupId: { in: programUserGroupIds } },
      },
    });
    if (skipAllowances) {
      q = q.expand([
        'approvalTree.approvalTreeUserGroups.programUserGroup.programUserGroupExclusions',
        'programBillingConfigs',
        'activeStatus',
        'programStartDateType',
      ]);
    } else {
      q = q.expand([
        // The PUG itself stays: getProgramUserItems reads atug.programUserGroup.activeStatusId.
        // Its membership rows do not, unless the caller asks.
        'approvalTree.approvalTreeUserGroups.programUserGroup',
        ...(includeUserGroupMaps ? ['approvalTree.approvalTreeUserGroups.programUserGroup.programUserGroupMaps'] : []),
        'approvalTree.approvalTreeUserGroups.approvalTreeAdminGroup.accountAdminGroup.accountAdminGroupMaps.accountAdmin.proximityUser',
        'programUserGroupExclusions',
        'programAllowances.programAllowanceUserGroupMaps',
        'programIssuances.programIssuanceUserGroupMaps.programUserGroup',
        'programIssuances.programProductTypeTag',
        'programBillingConfigs',
        'programSuppliers',
        'activeStatus',
        'programStartDateType',
      ]);
    }

    return q.execute();
  }

  async getProgramChangeState(programId: string) {
    const r = await this.uow
      .createQuery(Program)
      .where({
        id: programId,
      })
      .expand(['programProductTypeConfigs.productTypeConfig.pricedProductType.productType', 'activeStatus'])
      .execute();
    return r[0];
  }

  //#endregion

  //#region Other Program Related

  /** Get all Features used by all ProductTypeConfigs in the program
   * @param pricedOnly - only Features with nonzero prices */
  getFeaturesByProgram(programId: string, pricedOnly: boolean) {
    return this.uow.createQuery(Feature, 'FeaturesByProgram').withParameters({ programId, pricedOnly }).execute();
  }

  /** Get all Addons used by all ProductTypeConfigs in the program
   * @param pricedOnly - only Addons with nonzero prices */
  getAddonsByProgram(programId: string, pricedOnly: boolean) {
    return this.uow.createQuery(Addon, 'AddonsByProgram').withParameters({ programId, pricedOnly }).execute();
  }

  async getProgramViews(programId: string) {
    return this.uow.createQuery(ProgramView).where({ programId }).expand(['programViewProductTypeConfigs.productTypeConfig']).execute();
  }

  async getRapidOrders(programId: string) {
    return this.uow
      .createQuery(ProgramRapidTemplate)
      .where({ programId })
      .expand(['programRapidTemplateProductTypeConfigs.productTypeConfig'])
      .execute();
  }

  async getProgramPurchaseOrderTypes() {
    const r = await this.uow.createQuery(ProgramPurchaseOrderType).execute();
    return r;
  }

  //#endregion

  //#region ProgramUserGroup related

  async getProgramUserGroupsWithBudgets(accountId: string) {
    return this.uow
      .createQuery(ProgramUserGroup)
      .where({ accountId: accountId })
      .expand(['account', 'programUserGroupBudgets'])
      .orderBy('name')
      .execute();
  }

  async getProgramUserGroups(accountId: string, activeOnly: boolean = false) {
    if (activeOnly) {
      return this.uow
        .createQuery(ProgramUserGroup)
        .expand(['account'])
        .where({ accountId: accountId, activeStatusId: ActiveStatusEnum.Active })
        .execute();
    } else {
      return this.uow.createQuery(ProgramUserGroup).expand(['account']).where({ accountId: accountId }).execute();
    }
  }

  async getProgramUserGroupMapsWithDupUsers(programId: string) {
    const q = this.uow.createQuery(ProgramUserGroupMap, 'ProgramUserGroupMapsWithDupUsers').withParameters({ programId: programId });
    return q.execute();
  }

  async getProgramUserGroupsForApprovalTree(approvalTreeId: string) {
    return this.uow
      .createQuery(ProgramUserGroup)
      .where({
        approvalTreeUserGroups: { any: { approvalTreeId: approvalTreeId } },
      })
      .expand([
        'account',
        'approvalTreeUserGroups.approvalTree',
        // 'programUserGroupMaps.accountUser.proximityUser',
        'programUserGroupExclusions',
      ])
      .execute();
  }

  async getProgramUserGroupExclusionsForProgramUserGroupMap(pugm: ProgramUserGroupMap) {
    const r = await this.uow
      .createQuery(ProgramUserGroupExclusion)
      .where({ programUserGroupId: pugm.programUserGroupId, accountUserId: pugm.accountUserId })
      .execute();
    return r;
  }

  /**
   * Every (program x PUG) exclusion for one Account User. Loaded into the unit of work so the
   * governing-group chooser can delete existing exclusions as well as create new ones — picking
   * a winner is "include this one, exclude the rest", which needs both directions.
   */
  async getProgramUserGroupExclusionsForAccountUser(accountUserId: string) {
    return this.uow.createQuery(ProgramUserGroupExclusion).where({ accountUserId }).execute();
  }

  async getProgramUserGroupExclusionsForProgram(programId: string) {
    const r = await this.uow.createQuery(ProgramUserGroupExclusion).where({ programId }).execute();
    return r;
  }

  async getActiveProgramsForApprovalTree(atId: string) {
    const r = this.uow
      .createQuery(Program)
      .where({
        approvalTreeId: atId,
        activeStatusId: ActiveStatusEnum.Active,
      })
      .expand(['approvalTree.approvalTreeUserGroups.programUserGroup'])
      .execute();
    return r;
  }

  async getProgramsForApprovalTree(atId: string) {
    const r = this.uow
      .createQuery(Program)
      .where({
        approvalTreeId: atId,
      })
      .execute();
    return r;
  }

  async getProgramUserGroupMapsForUser(accountUserId: string) {
    const r = await this.uow
      .createQuery(ProgramUserGroupMap)
      .where({ accountUserId: accountUserId })
      .expand(['programUserGroup.programUserGroupExclusions'])
      .execute();
    return r;
  }

  /**
   * PUG memberships for one Program User Group.
   *
   * `includeShippingAddress` pulls in the AccountUser's shipping AccountAddress as well. Roster
   * screens need it because migrated accounts routinely leave ProximityUser.Email/Telephone/
   * Extension null and carry that contact info on the address row instead. Off by default so the
   * editing screens that only need names keep their smaller payload.
   */
  async getProgramUserGroupMaps(programUserGroupId: string, includeShippingAddress = false) {
    const expands = ['accountUser.proximityUser', 'programUserGroup.programUserGroupExclusions'];
    if (includeShippingAddress) {
      expands.push('accountUser.shippingAccountAddress');
    }
    const q = this.uow
      .createQuery(ProgramUserGroupMap)
      .where({
        programUserGroupId: programUserGroupId,
      })
      .expand(expands);

    return q.execute();
  }

  /**
   * Every non-Inactive AccountUser on an account, with the navigations an account-wide user
   * roster needs: ProximityUser (name + contact), the shipping AccountAddress (contact fallback
   * and address description), and PUG memberships for the "Program User Group" column.
   *
   * Status filter follows the standard rule - exclude only Inactive, keep Hold and Phaseout,
   * which admins expect to see. The exitDate cutoff is applied by the caller because it is
   * relative to "now" and cannot be expressed as a static predicate.
   */
  async getAccountUserRoster(accountId: string) {
    return this.uow
      .createQuery(AccountUser)
      .where({ accountId: accountId, 'proximityUser.activeStatusId': { ne: ActiveStatusEnum.Inactive } })
      .expand(['proximityUser', 'shippingAccountAddress', 'programUserGroupMaps.programUserGroup'])
      .execute();
  }

  async getShippingUserGroupMaps(shippingUserGroupId: string) {
    const q = this.uow
      .createQuery(ShippingUserGroupMap)
      .where({
        shippingUserGroupId: shippingUserGroupId,
      })
      .expand(['accountUser.proximityUser']);

    return q.execute();
  }

  //#endregion

  //#region ProgramIssuances and AccountIssuances

  async getAccountIssuances(accountId: string) {
    const r = await this.uow
      .createQuery(AccountIssuance)
      .where({ accountId })
      .expand(['accountIssuanceUserGroupMaps.programUserGroup', 'programProductTypeTag', 'programAccountIssuanceMaps.program'])
      .execute();
    return r;
  }

  async getProgramKitIssuances(programId: string) {
    return this.uow.createQuery(ProgramKitIssuance).where({ programId }).expand(['programKitIssuanceUserGroupMaps', 'kitTag']).execute();
  }

  async getProgramIssuances(programId: string) {
    const r = await this.uow
      .createQuery(ProgramIssuance)
      .where({ programId })
      .expand(['programIssuanceUserGroupMaps.programUserGroup', 'programProductTypeTag'])
      .execute();
    return r;
  }

  async getProgramIssuanceUserLogsByProgramAndUser(programId: string, accountUserId: string) {
    const q = this.uow
      .createQuery<ProgramIssuanceUserLog>(ProgramIssuanceUserLog, 'ProgramIssuanceUserLogsByProgramAndUser')
      .withParameters({ programId, accountUserId });
    return q.execute();
  }

  async getProgramIssuanceUserLogsByUser(accountUserId: string) {
    const q = this.uow
      .createQuery(ProgramIssuanceUserLog)
      .where({ accountUserId })
      .expand(['programIssuance.program', 'programIssuance.programProductTypeTag', 'jobOrder', 'jobOrder.programUserGroup']);
    return q.execute();
  }

  async getProgramIssuanceUserLogsByProgramIssuanceId(programIssuanceId: string) {
    const q = this.uow.createQuery(ProgramIssuanceUserLog).where({
      programIssuanceId,
    });
    const r = q.execute();
    return r;
  }

  async getAccountIssuanceUserLogsByAccountAndUser(accountId: string, accountUserId: string) {
    const q = this.uow
      .createQuery<AccountIssuanceUserLog>(AccountIssuanceUserLog, 'AccountIssuanceUserLogsByAccountAndUser')
      .withParameters({ accountId, accountUserId });
    return q.execute();
  }

  async getAccountIssuanceUserLogsByAccountIssuanceId(accountIssuanceId: string) {
    const q = this.uow.createQuery(AccountIssuanceUserLog).where({
      accountIssuanceId,
    });
    return q.execute();
  }

  async getAccountIssuancesForProgramUserGroups(accountId: string, programUserGroupIds: string[]) {
    return this.uow
      .createQuery(AccountIssuance)
      .where({
        accountId,
        accountIssuanceUserGroupMaps: {
          any: { programUserGroupId: { in: programUserGroupIds } },
        },
      })
      .expand(['accountIssuanceUserGroupMaps', 'programAccountIssuanceMaps', 'programProductTypeTag'])
      .execute();
  }

  //#endregion

  //#region ProgramAllowance and UserAllowance related

  async getProgramAllowances(programId: string) {
    return this.uow
      .createQuery(ProgramAllowance)
      .where({ programId })
      .expand(['programAllowanceUserGroupMaps.programUserGroup', 'programAllowanceAddonExceptions', 'programAllowanceFeatureExceptions'])
      .execute();
  }

  async getProgramAllowanceUserLogsByProgramAllowanceId(programAllowanceId: string) {
    const q = this.uow.createQuery(ProgramAllowanceUserLog).where({
      programAllowanceId,
    });
    const r = q.execute();
    return r;
  }

  async getProgramAllowanceUserLogsByProgramAllowanceIdWithUser(programAllowanceId: string) {
    const q = this.uow.createQuery(ProgramAllowanceUserLog).where({ programAllowanceId }).expand(['accountUser.proximityUser']);
    return q.execute();
  }

  async getProgramAllowanceUserLogsByProgramAndUser(programId: string, accountUserId: string) {
    const q = this.uow
      .createQuery(ProgramAllowanceUserLog, 'ProgramAllowanceUserLogsByProgramAndUser')
      .withParameters({ programId, accountUserId });
    return q.execute();
  }

  async getProgramAllowanceUserLogsByUser(accountUserId: string) {
    const q = this.uow.createQuery(ProgramAllowanceUserLog, 'ProgramAllowanceUserLogsByUser').withParameters({ accountUserId });
    return q.execute();
  }

  async getProgramAdjustmentUserLogsByUser(accountUserId: string) {
    const q = this.uow
      .createQuery(ProgramAllowanceUserLog, 'ProgramAdjustmentUserLogsByUser')
      .expand(['accountAdmin', 'accountAdmin.proximityUser'])
      .withParameters({ accountUserId });
    return q.execute();
  }

  /** withJobOrder: only the ledger DISPLAYS the order behind a draw; the balance callers below
   *  just sum amounts, and should not pull a JobOrder per row to do it. */
  async getAccountUserAllowanceLogsByUser(accountUserId: string, withJobOrder = false) {
    const expands = ['accountAdmin', 'accountAdmin.proximityUser'];
    if (withJobOrder) expands.push('toJobOrder');
    const q = this.uow.createQuery(AccountUserAllowanceLog).expand(expands).where({ accountUserId });
    return q.execute();
  }

  async getAccountUserAllowanceRemaining(accountUserId: string) {
    const logs = await this.getAccountUserAllowanceLogsByUser(accountUserId);
    const remainingAmt = _.sum(logs.map((l) => l.amt));
    return remainingAmt;
  }

  async getProgramAllowanceUserLogsForUserByPrograms(accountUserId: string, programIds: string[]) {
    return this.uow
      .createQuery(ProgramAllowanceUserLog)
      .where({
        accountUserId,
        'programAllowance.programId': { in: programIds },
      })
      .expand(['programAllowance'])
      .execute();
  }

  //#endregion

  //#region Budgets

  async getAccountBudgets(accountId: string) {
    return this.uow.createQuery(AccountBudget).where({ accountId }).expand(['budgetProductTypeTag']).execute();
  }

  async getProgramUserGroupBudgets(programUserGroupId: string) {
    return this.uow
      .createQuery(ProgramUserGroupBudget)
      .where({ programUserGroupId: programUserGroupId })
      .expand(['budgetProductTypeTag'])
      .execute();
  }

  /** The AI guideline masters and every override in the chain of one program. GlobalAdmin only (server). */
  async getGuidelinesForProgram(programId: string): Promise<Guideline[]> {
    return this.uow.createQuery<Guideline>(Guideline, 'GuidelinesForProgram').withParameters({ programId }).execute();
  }

  /** The AI guideline masters, the supplier overrides, and the overrides of one account and its programs. GlobalAdmin only (server). */
  async getGuidelinesForAccount(accountId: string): Promise<Guideline[]> {
    return this.uow.createQuery<Guideline>(Guideline, 'GuidelinesForAccount').withParameters({ accountId }).execute();
  }

  async getProgramBudgets(programId: string) {
    return this.uow.createQuery(ProgramBudget).where({ programId }).expand(['budgetProductTypeTag']).execute();
  }

  async isProgramProductTypeTagUsed(programProductTypeTagId: string) {
    const r = await this.uow.createQuery(ProgramIssuance).where({ programProductTypeTagId: programProductTypeTagId }).take(1).execute();
    return r.length > 1;
  }

  async isBudgetProductTypeTagUsed(budgetProductTypeTagId: string) {
    const p1 = this.uow.createQuery(ProgramBudget).where({ budgetProductTypeTagId: budgetProductTypeTagId }).take(1).execute();
    // const r = await p1;
    const p2 = this.uow.createQuery(ProgramUserGroupBudget).where({ budgetProductTypeTagId: budgetProductTypeTagId }).take(1).execute();
    const p3 = this.uow.createQuery(AccountBudget).where({ budgetProductTypeTagId: budgetProductTypeTagId }).take(1).execute();
    const results = await Promise.all([p1, p2, p3]);
    return results.some((r) => r.length > 0);
  }

  //#endregion

  //#region JobOrder related

  async getProductTypeConfigsByProgramId(programId: string) {
    const r = await this.uow
      .createQuery(ProductTypeConfig)
      .where({
        programProductTypeConfigs: {
          any: { programId: programId },
        },
      })
      .expand([
        'programProductTypeConfigs.program',
        'productTypeConfigProducts.product.productType',
        'productTypeConfigProducts.product.productFeatureChoices.featureChoice.feature',
      ])
      .execute();
    return r;
  }

  async getJobOrderById(jobOrderId: string) {
    const jo = await this.findJobOrderById(jobOrderId);
    UtilFns.assertNonNull(jo, `JobOrder ${jobOrderId}`);
    return jo;
  }

  /** Undefined when the job order does not exist, e.g. a deleted draft opened from an old link. */
  async findJobOrderById(jobOrderId: string): Promise<JobOrder | undefined> {
    const r = await this.uow
      .createQuery(JobOrder)
      .where({ id: jobOrderId })
      .expand([
        'account',
        'accountUser',
        // 'program.programAllowances.programAllowanceUserGroupMaps.programUserGroup',
        'accountUser.proximityUser',
        'accountProcurementCard',
        // Shown on the Order Information tab, and the value JobOrderRulesService.buildState
        // re-stamps when the user's governing group changes -- so it has to be loaded to be seen.
        'programUserGroup',
        'jobOrderDetails',
        'jobOrderDetails.jobOrderDetailAddons.pricedAddon.addon',
        'jobOrderDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.productType.manufacturer',
        'jobOrderDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.productType.productTypeFeatures',
        'jobOrderDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.pricedProductTypeFeatureChoices',
        'jobOrderDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.programProductTypeTagMaps.programProductTypeTag',
        'jobOrderDetails.productTypeConfigProduct.productTypeConfig.productTypeConfigAddons.pricedAddon',
        'jobOrderDetails.productTypeConfigProduct.product.productFeatureChoices.featureChoice.feature',
        'jobOrderDetails.productTypeConfigProduct.product.productType.supplier',
      ])
      .execute();
    UtilFns.assertArrayZeroOrOne(r);
    return r[0];
  }

  // Server side query filter should limit this to a single account
  createJobOrdersQuery() {
    const r = this.uow
      .createQuery(JobOrder)

      .expand([
        'program',
        'account',
        'jobOrderStatus',
        'shippingAccountAddress',
        'accountUser.proximityUser',
        'approvedByAccountAdmin.proximityUser',
      ]);
    return r;
  }

  // createJobOrdersByStatusQuery(joStatusId: number) {
  //   const r = this.uow
  //     .createQuery(JobOrder)
  //     .where({ jobOrderStatusId: joStatusId })
  //     .expand([
  //       'program',
  //       'account',
  //       'jobOrderStatus',
  //       'shippingAccountAddress',
  //       'accountUser.proximityUser',
  //       'approvedByAccountAdmin.proximityUser',
  //     ]);
  //   return r;
  // }

  createJobOrdersByAccountAdminIdQuery(accountAdminId: string, includeHierarchy: boolean) {
    const q = this.uow.createQuery<JobOrder>(JobOrder, 'JobOrdersByAccountAdminId').withParameters({
      accountAdminId: accountAdminId,
      includeHierarchy: includeHierarchy,
    });
    return q;
  }

  async getJobOrdersByAccountAdminId(accountAdminId: string, includeHierarchy: boolean) {
    const q = this.uow.createQuery<JobOrder>(JobOrder, 'JobOrdersByAccountAdminId').withParameters({
      accountAdminId: accountAdminId,
      includeHierarchy: includeHierarchy,
    });
    return q.execute();
  }

  /**
   * Orders this user has placed on a program since a given date — the test behind the
   * one-order-per-collection rule (Docs/Collect-Organize-Place.md §4b).
   *
   * Keyed on OrderPlacedTs because the rule is about orders PLACED, not started. An order still
   * being built has no placement stamp and is already handled elsewhere, by the existing rule that
   * a user may not have two open orders on one program.
   */
  async getPlacedJobOrdersForUserAndProgramSince(accountUserId: string, programId: string, since: Date) {
    return this.uow
      .createQuery(JobOrder)
      .where({
        accountUserId: accountUserId,
        programId: programId,
        orderPlacedTs: { ge: since },
      })
      .execute();
  }

  async getJobOrdersByAccountUser(accountUserId: string) {
    return this.uow
      .createQuery(JobOrder)
      .where({
        accountUserId: accountUserId,
      })
      .expand(['program.approvalTree.approvalTreeUserGroups.programUserGroup', 'jobOrderDetails', 'approvedByAccountAdmin'])
      .execute(true);
  }

  async getJobOrdersByAccountUserIdExt(accountUserId: string) {
    const r = await this.uow
      .createQuery(JobOrder)
      .where({ accountUserId: accountUserId, orderPlacedTs: null })
      .orderBy('displayId')
      .expand([
        'jobOrderDetails',
        'jobOrderDetails.productTypeConfigProduct.productTypeConfig.pricedProductType',
        'jobOrderDetails.productTypeConfigProduct.product.productType.manufacturer',
        'jobOrderDetails.productTypeConfigProduct.product.productType.supplier',
        'jobOrderDetails.productTypeConfigProduct.product.productType.productTypeFeatures',
        'jobOrderDetails.productTypeConfigProduct.product.productFeatureChoices.featureChoice.feature',
        'jobOrderDetails.jobOrderDetailAddons.pricedAddon.addon',
        'jobOrderDetails.programKit.kit',
        'program',
        'programUserGroup',
        'account',
        'shippingAccountAddress',
        'accountUser.proximityUser',
        'accountPurchaseOrder',
        'jobOrderOveragePayments',
      ])
      .execute();
    return r;
  }

  async getJobOrdersPlacedByAccountUser(accountUserId: string) {
    return this.uow
      .createQuery(JobOrder)
      .where({
        accountUserId: accountUserId,
        jobOrderStatusId: { in: [JobOrderStatusEnum.Placed, JobOrderStatusEnum.Closed] }, // TODO: may need to include closed JobOrders
      })
      .expand(['program', 'jobOrderHistDetails.product.productType.manufacturer'])
      .execute();
  }

  async getJobOrdersForUserAndProgram(accountUserId: string, programId: string) {
    return this.uow
      .createQuery(JobOrder)
      .where({
        accountUserId: accountUserId,
        programId: programId,
        jobOrderStatusId: { in: [JobOrderStatusEnum.Placed, JobOrderStatusEnum.Closed] }, // TODO: may need to include closed JobOrders
      })
      .execute();
  }

  /**
   * Every in-flight order for this user, across all programs.
   *
   * Used when re-pointing a user's shipping address during collection review: any order of theirs
   * that is still unplaced or unapproved would otherwise keep the old address and ship to where
   * the user no longer is.
   */
  async getUnplacedJobOrdersForUser(accountUserId: string) {
    return this.uow
      .createQuery(JobOrder)
      .where({ accountUserId: accountUserId, jobOrderStatusId: { in: UNPLACED_STATUSES } })
      .execute();
  }

  /**
   * Every in-flight order that blocks removing this user from this Program User Group.
   * Uses UNPLACED_STATUSES so the client asks exactly the question the server's
   * CheckProgramUserGroupMapDeletes strand-guard answers — letting the UI name the blocking
   * orders up front instead of offering a removal the save would reject.
   */
  async getUnplacedJobOrdersForUserAndProgramUserGroup(accountUserId: string, programUserGroupId: string) {
    return this.uow
      .createQuery(JobOrder)
      .where({
        accountUserId: accountUserId,
        programUserGroupId: programUserGroupId,
        jobOrderStatusId: { in: UNPLACED_STATUSES },
      })
      .execute();
  }

  async getJobOrderDetailById(jobOrderDetailId: string) {
    const r = await this.uow
      .createQuery(JobOrderDetail)
      .where({ id: jobOrderDetailId })
      .expand(['jobOrder', 'jobOrderDetailAddons'])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getJobOrderOveragePayments(jobOrderId: string) {
    return this.uow.createQuery(JobOrderOveragePayment).where({ jobOrderId: jobOrderId }).execute();
  }

  async getJobOrderNotes(jobOrderId: string) {
    return this.uow.createQuery(JobOrderNote).where({ jobOrderId: jobOrderId }).orderBy('noteTs', true).expand(['proximityUser']).execute();
  }

  async getJobOrderDetailAddOnsByJobOrderId(jobOrderId: string) {
    const r = this.uow
      .createQuery(JobOrderDetailAddon)
      .where({ 'jobOrderDetail.jobOrderId': jobOrderId })
      .expand(['jobOrderDetail'])
      .execute();
    return r;
  }

  async getJobOrderDetailStatuses(jobOrderId: string) {
    // GetJobOrderDetailStatuses at this level returns a JobOrderHistDetail with status fields populated.
    const r = await this.uow.createQuery(JobOrderHistDetail, 'GetJobOrderDetailStatuses').withParameters({ jobOrderId }).execute(true);
    return r;
  }

  async getJobOrderDetailCascade(productTypeConfigId: string, programId: string) {
    const r = await this.uow
      .createQuery(ProductTypeConfigProduct)
      .where({ 'productTypeConfig.id': productTypeConfigId })
      .expand([
        'productTypeConfig.pricedProductType.account',
        'productTypeConfig.programProductTypeConfigs.program',
        'product.productFeatureChoices.featureChoice.feature',
        'product.productType',
      ])
      .execute();
    return r;
  }

  //#endregion

  //#region JobOrderHist

  async getJobOrderHistSummaries(whereObject: any) {
    const r = await this.uow.createQuery(JobOrder, 'JoHistSummaries').where(whereObject).orderBy('displayId').execute();
    return r.map((x) => mapObject(x) as unknown as JoHistSummary);
  }

  async getJobOrderHistTotalsBySupplier(whereObject: any) {
    const r = await this.uow.createQuery(JobOrderHistDetail, 'JoHistTotalsBySupplier').where(whereObject).execute();
    return r[0]['totals'].map((x) => mapObject(x)) as JoHistTotalsBySupplier[];
  }

  async getJobOrderHistById(jobOrderId: string) {
    const r = await this.uow
      .createQuery(JobOrder)
      .where({ id: jobOrderId })
      .expand([
        'jobOrderHistDetails',
        'jobOrderHistDetails.jobOrderHistDetailFeatures.featureChoice.feature',
        'jobOrderHistDetails.jobOrderHistDetailAddons.addon',
        'jobOrderHistDetails.product.productType.manufacturer',
        'jobOrderHistDetails.product.productType.manufacturer.supplier',
        'jobOrderHistDetails.product.productFeatureChoices.featureChoice.feature',
        'program',
        'account',
        'shippingAccountAddress',
        'accountUser.proximityUser',
        'approvedByAccountAdmin.proximityUser',
        'jobOrderOveragePayments',
      ])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  async getJobOrderHistByAccountUserId(accountUserId: string) {
    const r = await this.uow
      .createQuery(JobOrder)
      .where({ accountUserId: accountUserId })
      .orderBy('displayId')
      .expand([
        'jobOrderHistDetails',
        'jobOrderHistDetails.jobOrderHistDetailFeatures.featureChoice.feature',
        'jobOrderHistDetails.jobOrderHistDetailAddons.addon',
        'jobOrderHistDetails.product.productType.productTypeFeatures',
        'jobOrderHistDetails.product.productType.manufacturer',
        'jobOrderHistDetails.product.productType.supplier',
        'jobOrderHistDetails.product.productFeatureChoices.featureChoice.feature',
        'jobOrderHistDetails.cancellationRequestDetails',
        'jobOrderHistDetails.returnRequestDetails',
        'program',
        'programUserGroup',
        'account',
        'shippingAccountAddress',
        'accountUser.proximityUser',
        'approvedByAccountAdmin.proximityUser',
        'jobOrderOveragePayments',
        'accountPurchaseOrder',
      ])
      .execute();
    return r;
  }

  /** Gets all placed order detail rows for an account user (queries JobOrderHistDetail directly). */
  async getJobOrderHistDetailsByAccountUserId(accountUserId: string) {
    return this.uow
      .createQuery(JobOrderHistDetail)
      .where({
        'jobOrder.accountUserId': accountUserId,
      })
      .expand([
        'jobOrder.program',
        'jobOrder.account',
        'jobOrder.shippingAccountAddress',
        'jobOrder.accountUser.proximityUser',
        'jobOrder.approvedByAccountAdmin.proximityUser',
        'jobOrder.accountPurchaseOrder',
        'jobOrder.jobOrderOveragePayments',
        'jobOrderHistDetailFeatures.featureChoice.feature',
        'jobOrderHistDetailAddons.addon',
        'product.productType.manufacturer',
        'product.productType.supplier',
        'product.productType.productTypeFeatures',
        'product.productFeatureChoices.featureChoice.feature',
        'returnPolicy',
        'returnRequestDetails',
        'cancellationRequestDetails.cancellationRequest',
      ])
      .execute();
  }

  createJobOrderHistByAccountIdQuery(accountId: string) {
    const r = this.uow
      .createQuery(JobOrderHistDetail)
      .where({
        'jobOrder.accountId': accountId,
        'jobOrder.orderPlacedTs': { gt: new Date('2025-01-01') },
      })
      .expand([
        'jobOrder',
        'jobOrderHistDetailFeatures.featureChoice.feature',
        'jobOrderHistDetailAddons.addon',
        'product.productType.manufacturer',
        'product.productType.supplier',
      ]);
    return r;
  }

  /** For Placed Order page - everything but the details */
  async getJobOrderForHistPage(jobOrderId: string) {
    const r = await this.uow
      .createQuery(JobOrder)
      .where({ id: jobOrderId })
      .expand([
        'program',
        'account',
        'shippingAccountAddress',
        'accountUser.proximityUser',
        'approvedByAccountAdmin.proximityUser',
        'returnRequests',
        'accountPurchaseOrder',
        'cancellationRequests',
      ])
      .execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  /** For Placed Order page - only the details */
  async getJobOrderHistDetailsForHistPage(jobOrderId: string) {
    return this.uow
      .createQuery(JobOrderHistDetail)
      .where({ jobOrderId })
      .expand([
        'jobOrderHistDetailFeatures',
        'jobOrderHistDetailAddons.addon',
        'product.productType.productTypeFeatures',
        'product.productType.manufacturer.supplier',
        'product.productFeatureChoices.featureChoice.feature',
        'cancellationRequestDetails',
        'returnRequestDetails.returnRequest',
        'returnRequestDetails.returnReason.returnReasonType',
        'returnPolicy',
      ])
      .execute();
  }

  /** For the allowance subledger dialog - the lines behind one ProgramAllowanceUserLog charge.
   *  Expands feature/addon children (and their names) because the dialog itemises exactly which
   *  oversize choices and addons the allowance excluded. */
  async getJobOrderHistDetailsForAllowanceSubledger(jobOrderId: string) {
    return this.uow
      .createQuery(JobOrderHistDetail)
      .where({ jobOrderId })
      .expand([
        'jobOrderHistDetailFeatures.featureChoice.feature',
        'jobOrderHistDetailAddons.addon',
        'product.productType',
        // Overage payments reduce the allowance charge (ProgramAllowanceWrapper.curAmt), so the
        // dialog needs them to reconcile the posted amount without reporting a false variance.
        'jobOrder.jobOrderOveragePayments',
      ])
      .execute();
  }

  async getJobOrderSummariesForAccount(accountId: string) {
    const q = this.uow.createQuery(JobOrderSummaryDto, 'GetJobOrderSummariesForAccount').withParameters({ accountId });
    return q.execute();
  }

  //#endregion

  //#region ReturnRequest related

  async getReturnRequestById(returnRequestId: string) {
    const r = await this.uow
      .createQuery(ReturnRequest)
      .where({
        id: returnRequestId,
      })
      .expand(['returnRequestDetails', 'jobOrder.account'])
      .execute();
    return _.first(r);
  }

  async getReturnRequestDetailsForJobOrder(jobOrderId: string) {
    const r = await this.uow
      .createQuery(ReturnRequestDetail)
      .where({
        'returnRequest.jobOrderId': jobOrderId,
      })
      .expand(['returnRequest'])
      .execute();
    return r;
  }

  async getOpenReturnRequestsForJobOrder(jobOrderId: string) {
    const r = await this.uow
      .createQuery(ReturnRequest)
      .where({
        jobOrderId: jobOrderId,
        returnRequestStatusId: ReturnRequestStatusEnum.Setup,
      })
      .execute();
    return _.first(r);
  }

  async getReturnReasons(returnPolicyId: string) {
    const r = await this.uow.createQuery(ReturnReason).where({ returnPolicyId: returnPolicyId }).orderBy('name').execute();
    return r;
  }

  async getReturnPolicies(supplierId: string) {
    const r = await this.uow.createQuery(ReturnPolicy).where({ supplierId: supplierId }).execute();
    // Exclude migration placeholder policy (created by Prox-migrate-3.sql)
    return r.filter(rp => rp.name !== 'Placeholder for migrations');
  }

  async getSupplierReturnRequestDetailStatuses(returnRequestId: string) {
    const r = await this.uow
      .createQuery(ReturnRequestDetail, 'GetSupplierReturnRequestDetailStatuses')
      .withParameters({ returnRequestId })
      .execute(true);
    return r;
  }

  //#endregion

  //#region CancellationRequests

  async getCancellationRequestById(cancellationRequestId: string) {
    const r = await this.uow
      .createQuery(CancellationRequest)
      .where({
        id: cancellationRequestId,
      })
      .expand(['cancellationRequestDetails', 'jobOrder.account'])
      .execute();
    return _.first(r);
  }

  async getOpenCancellationRequestsForJobOrder(jobOrderId: string) {
    const r = await this.uow
      .createQuery(CancellationRequest)
      .where({
        jobOrderId: jobOrderId,
        cancellationRequestStatusId: CancellationRequestStatusEnum.Setup,
      })
      .execute();
    return _.first(r);
  }

  async getCancellationRequestDetailsForJobOrder(jobOrderId: string) {
    const r = await this.uow
      .createQuery(CancellationRequestDetail)
      .where({
        'cancellationRequest.jobOrderId': jobOrderId,
      })
      .expand(['cancellationRequest'])
      .execute();
    return r;
  }

  async getSupplierCancellationRequestDetailStatuses(cancellationRequestId: string) {
    const r = await this.uow
      .createQuery(CancellationRequestDetail, 'GetSupplierCancellationRequestDetailStatuses')
      .withParameters({ cancellationRequestId: cancellationRequestId })
      .execute(true);
    return r;
  }

  //#endregion

  //#region Invoices

  /**
   * Get invoices for a job order from supplier APIs (e.g., Unitec).
   * Throws an error with message if supplier server is offline.
   */
  async getInvoicesForJobOrder(jobOrderId: string): Promise<InvoiceHeaderDTO[]> {
    const query = EntityQuery.from('GetInvoicesForJobOrder').withParameters({ jobOrderId });
    try {
      const queryResult = await this.uow.manager.executeQuery(query);
      const result = queryResult.results[0] as InvoiceQueryResultDTO;

      if (!result.success) {
        throw new Error(result.errorMessage || 'Supplier server may be offline or unavailable');
      }

      // parseDateFromServer turns null into an Invalid Date, which makes the date pipe throw
      // (an invoice that has not shipped has no shipTs).
      const parseDate = (value: any) => (value == null ? null : DataType.parseDateFromServer(value));

      return result.invoices.map((invoice) => {
        return {
          ...invoice,
          invoiceDate: parseDate(invoice.invoiceDate),
          dueDate: parseDate(invoice.dueDate),
          shipTs: parseDate(invoice.shipTs),
          shipExpirationDate: parseDate(invoice.shipExpirationDate),
          createdTs: parseDate(invoice.createdTs),
          invoiceDetails: invoice.invoiceDetails,
        } as InvoiceHeaderDTO;
      });
    } catch (error: any) {
      throw new Error(error.message || 'Supplier server may be offline or unavailable');
    }
  }

  // No longer needed - Marcel's version of parseDate
  // _parseDateRegEx: RegExp | null = null;
  // parseDate(dateStr: any): Date | null {
  //   if (typeof dateStr !== 'string') return dateStr;
  //   if (this._parseDateRegEx == null) {
  //     this._parseDateRegEx =
  //       // eslint-disable-next-line max-len
  //       /^([\+-]?\d{4}(?!\d{2}\b))((-?)((0[1-9]|1[0-2])(\3([12]\d|0[1-9]|3[01]))?|W([0-4]\d|5[0-2])(-?[1-7])?|(00[1-9]|0[1-9]\d|[12]\d{2}|3([0-5]\d|6[1-6])))([T\s]((([01]\d|2[0-3])((:?)[0-5]\d)?|24\:?00)([\.,]\d+(?!:))?)?(\17[0-5]\d([\.,]\d+)?)?([zZ]|([\+-])([01]\d|2[0-3]):?([0-5]\d)?)?)?)?$/;
  //   }
  //   return this._parseDateRegEx.test(dateStr) ? new Date(dateStr) : null;
  // }

  fixupInvoices(jobOrder: JobOrder, invoiceHeaderDTOs: InvoiceHeaderDTO[]): InvoiceHeaderDTO[] {
    // clear out preexisting invoiceDetails
    jobOrder.jobOrderHistDetails.forEach((johd) => {
      johd.invoiceDetails = [];
    });

    // create a Map of JohdIds to JobOrderHistDetails
    const johdMap = new Map<string, JobOrderHistDetail>(jobOrder.jobOrderHistDetails.map((johd) => [johd.id, johd]));

    invoiceHeaderDTOs.forEach((inv) => {
      // update all of the invoiceDetails within an invoice
      inv.invoiceDetails.forEach((invd) => {
        invd.invoiceHeader = inv;
        const johd = johdMap.get(invd.joDetailId);
        if (johd != null) {
          invd.jobOrderHistDetail = johd;
          johd.invoiceDetails.push(invd);
        }
      });
    });
    return invoiceHeaderDTOs;
  }

  /**
   * Fan-out fetch of GetInvoicesForJobOrder across many JobOrders, running fixupInvoices
   * on each success. Returns a summary describing partial failures. On any failure this
   * helper adds a persistent NotificationService banner keyed as `supplier-unavailable`;
   * on full success it resolves that key. Silent failure is impossible.
   *
   * This is the single choke point for "did we successfully learn shipping/invoicing state
   * from the supplier(s) attached to these orders?" — consumers must not roll their own
   * Promise.allSettled loop.
   */
  async fetchInvoicesForJobOrders(jobOrders: JobOrder[]): Promise<SupplierFetchResult> {
    if (jobOrders.length === 0) {
      return { ok: true, totalCount: 0, failedCount: 0 };
    }
    const results = await Promise.allSettled(jobOrders.map((jo) => this.getInvoicesForJobOrder(jo.id)));
    let failedCount = 0;
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') {
        this.fixupInvoices(jobOrders[i], r.value);
      } else {
        failedCount++;
      }
    });
    return this.buildSupplierFetchResult(jobOrders.length, failedCount, 'shipping/invoice information');
  }

  /**
   * Fan-out fetch of GetJobOrderDetailStatuses across many JobOrders. Breeze cache-merges
   * the returned JobOrderHistDetails, so callers holding johd references automatically pick
   * up any populated fields (SupplierExpectedShipDate, SupplierCancelQty, ...). See
   * fetchInvoicesForJobOrders for the failure-summary and banner contract.
   */
  async fetchJobOrderDetailStatusesForJobOrders(jobOrders: JobOrder[]): Promise<SupplierFetchResult> {
    if (jobOrders.length === 0) {
      return { ok: true, totalCount: 0, failedCount: 0 };
    }
    const results = await Promise.allSettled(jobOrders.map((jo) => this.getJobOrderDetailStatuses(jo.id)));
    const failedCount = results.filter((r) => r.status === 'rejected').length;
    return this.buildSupplierFetchResult(jobOrders.length, failedCount, 'supplier status');
  }

  /**
   * Shared key used by every supplier fan-out helper. All supplier-unavailable states
   * collapse into one banner so a single reload clears everything when the supplier(s)
   * come back online.
   */
  private static readonly SUPPLIER_UNAVAILABLE_KEY = 'supplier-unavailable';

  private buildSupplierFetchResult(totalCount: number, failedCount: number, subject: string): SupplierFetchResult {
    if (failedCount === 0) {
      this.notificationService.resolveByKey(AccountDbQueryService.SUPPLIER_UNAVAILABLE_KEY);
      return { ok: true, totalCount, failedCount: 0 };
    }
    const message =
      failedCount === totalCount
        ? `Supplier server unavailable — ${subject} could not be loaded. Quantities may be incomplete.`
        : `Some suppliers are unavailable — ${subject} for ${failedCount} of ${totalCount} orders could not be loaded. Quantities may be incomplete.`;
    this.notificationService.add(
      'warn',
      'Supplier Unavailable',
      message,
      undefined,
      AccountDbQueryService.SUPPLIER_UNAVAILABLE_KEY,
    );
    return { ok: false, totalCount, failedCount, message };
  }

  tryParseDate(value: any): Date | undefined {
    const date = new Date(value);
    return isFinite(date.getTime()) ? date : undefined;
  }

  async getInvoiceSummariesForAccount(accountId: string) {
    const q = this.uow.createQuery(InvoiceSummaryDto, 'GetInvoiceSummariesForAccount').withParameters({ accountId });
    return q.execute();
  }

  //#endregion

  //#region JobOrderBudgetLog

  async getJobOrderBudgetLogsForAccount(accountId: string, fiscalStartDate: Date) {
    const q = this.uow
      .createQuery<JobOrderBudgetLog>(JobOrderBudgetLog, 'JobOrderBudgetLogsForAccount')
      .withParameters({ accountId, fiscalStartDate });
    return q.execute();
  }

  async getJobOrderBudgetLogsForProgramUserGroup(programUserGroupId: string, fiscalStartDate: Date) {
    const q = this.uow
      .createQuery<JobOrderBudgetLog>(JobOrderBudgetLog, 'JobOrderBudgetLogsForProgramUserGroup')
      .withParameters({ programUserGroupId, fiscalStartDate });
    return q.execute();
  }

  async getJobOrderBudgetLogsForProgram(programId: string, fiscalStartDate: Date) {
    const q = this.uow
      .createQuery<JobOrderBudgetLog>(JobOrderBudgetLog, 'JobOrderBudgetLogsForProgram')
      .withParameters({ programId, fiscalStartDate });
    return q.execute();
  }

  //#endregion

  //#region ProximityUserNotes, ProximityRights and ProximityUserRights

  async getProximityUserNotes(proximityUserId: string) {
    return this.uow.createQuery(ProximityUserNote).where({ proximityUserId }).execute();
  }

  async getProximityRights() {
    return this.uow.getAllOrQuery(ProximityRight);
  }
  async getProximityRoles() {
    return this.uow.getAllOrQuery(ProximityRole);
  }
  async getProximityRoleRights() {
    return this.uow.getAllOrQuery(ProximityRoleRightMap);
  }

  //#endregion

  //#region Notifications

  async getNotificationEventAccountMaps(accountId: string) {
    return this.uow.createQuery(NotificationEventAccountMap).where({ accountId }).execute();
  }

  /** gets all of the notificationTemplates for a given account along with all of the
   * 'global templates */
  async getNotificationTemplates(accountId?: string) {
    const q = this.uow.createQuery(NotificationTemplate).where({
      or: [{ accountId: accountId }, { accountId: null }],
    });
    return q.execute();
  }

  async getNamedNotificationTemplates(accountId: string) {
    // isActive filter excludes templates the admin has retired. Breeze sends this through as a server-side
    // predicate, so the picker never sees inactive templates regardless of cache state.
    const q = this.uow.createQuery(NotificationTemplate).where({
      templateName: { ne: null },
      isActive: true,
      or: [{ accountId: accountId }, { accountId: null }],
    } as any);
    return q.execute();
  }

  async getInAppNotifications(proximityUserId: string, newOnly: boolean = false) {
    const where = { proximityUserId: proximityUserId };
    const whereNew = newOnly ? { wasRead: false } : {};
    const r = this.uow
      .createQuery(InAppNotification)
      .where({ ...where, ...whereNew })
      .expand(['notificationTemplate.notificationEvent', 'proximityUser'])
      .execute();

    return r;
  }

  async getInAppNotificationsCount(proximityUserId: string, newOnly: boolean = false) {
    const where = { proximityUserId: proximityUserId };
    const whereNew = newOnly ? { wasRead: false } : {};
    const r = this.uow
      .createQuery(InAppNotification)
      .where({ ...where, ...whereNew })
      .executeCount();
    return r;
  }

  async getInAppNotificationsForMany(proximityUserIds: string[]) {
    // Batch the queries to avoid URL length limits with large user lists
    const BATCH_SIZE = 50;
    const results: InAppNotification[] = [];

    for (let i = 0; i < proximityUserIds.length; i += BATCH_SIZE) {
      const batch = proximityUserIds.slice(i, i + BATCH_SIZE);
      const batchResults = await this.uow
        .createQuery(InAppNotification)
        .where({
          proximityUserId: { in: batch },
        })
        .expand(['notificationTemplate.notificationEvent', 'proximityUser'])
        .execute();
      results.push(...batchResults);
    }

    return results;
  }

  async getNotificationSubmissionsAfter(afterDate: Date) {
    const r = this.uow
      .createQuery(NotificationSubmission)
      .where({ createdTs: { ge: afterDate } })
      .expand(['notificationTemplate.notificationEvent', 'proximityUser', 'account'])
      .execute();
    return r;
  }

  //#endregion

  //#region Addresses

  // async getAccountAddresses(accountId: string, isBilling: boolean, isShipping: boolean, isPersonal: boolean ) {
  //   const r = await this.uow.createQuery(AccountAddress)
  //     .where({
  //       accountId: accountId,
  //       isBillingAddress: isBilling,
  //       isShippingAddress: isShipping,
  //       isPersonalAddress: isPersonal
  //      })
  //     .execute();
  //   return r;
  // }

  async getAccountAddress(accountAddressId: string) {
    const r = await this.uow.createQuery(AccountAddress).expand(['account.supplierAccounts']).where({ id: accountAddressId }).execute();
    UtilFns.assertArrayOfOne(r);
    return r[0];
  }

  //#endregion

  //#region ProductTypeConfigs

  /** Retrive ProductTypeConfig with feature choices and addons */
  async getProductTypeConfig(productTypeConfigId: string) {
    const q = this.uow
      .createQuery(ProductTypeConfig)
      .where({ id: productTypeConfigId })
      .expand([
        'productTypeConfigProducts.product.productFeatureChoices',
        'productTypeConfigAddons.pricedAddon.addon',
        'pricedProductType.pricedProductTypeFeatureChoices',
        'pricedProductType.productType.productTypeFeatures.feature.featureChoices',
      ]);
    const r = await q.execute();
    return _.first(r);
  }

  async getProductTypeConfigById(productTypeConfigId: string) {
    const r = await this.uow
      .createQuery(ProductTypeConfig)
      .where({ id: productTypeConfigId })
      .expand([
        'productTypeConfigProducts.product.productFeatureChoices.featureChoice',
        'productTypeConfigAddons.pricedAddon.addon',
        'pricedProductType.pricedProductTypeFeatureChoices',
        'pricedProductType.productType.supplier',
        'pricedProductType.productType.manufacturer',
        'pricedProductType.productType.productTypeFeatures.feature.featureChoices',
        'returnPolicy',
      ])
      .execute();
    UtilFns.assertArrayOfOne(r);
    const t = r[0];
    return r[0];
  }

  /** Fetch all ProductTypeConfigs for a program with full details for consolidation */
  async getProductTypeConfigsForProgram(programId: string): Promise<ProductTypeConfig[]> {
    return this.uow
      .createQuery(ProductTypeConfig)
      .where({ programProductTypeConfigs: { any: { programId } } })
      .expand([
        'productTypeConfigProducts.product.productFeatureChoices.featureChoice',
        'productTypeConfigAddons.pricedAddon.addon',
        'pricedProductType.pricedProductTypeFeatureChoices',
        'pricedProductType.productType.supplier',
        'pricedProductType.productType.manufacturer',
        'pricedProductType.productType.productTypeFeatures.feature.featureChoices',
        'returnPolicy',
      ])
      .execute();
  }

  /** Fetch all sibling ProductTypeConfigs under the given PricedProductTypes, with the addon
   *  and member-product collections needed to compute the Consolidate Me identity match
   *  (same PPT + same PricedAddon set + same active member Product set). */
  async getProductTypeConfigsForPricedProductTypes(pricedProductTypeIds: string[]): Promise<ProductTypeConfig[]> {
    if (pricedProductTypeIds.length === 0) return [];
    return this.uow
      .createQuery(ProductTypeConfig)
      .where({ pricedProductTypeId: { in: pricedProductTypeIds } })
      .expand([
        'pricedProductType.productType',
        'activeStatus',
        'productTypeConfigAddons',
        'productTypeConfigProducts.product',
      ])
      .execute();
  }

  /*   async getProductTypeConfigByProductTypeId(productTypeId: string, programId: string) {
    const r
  } */

  async getProgramProductTypeConfigsByProgram(programId: string) {
    // NOTE - feature choices and add-ons were remove from server-side query.
    // They are retrieved on demand using the getProductTypeConfig method below.
    const q = this.uow
      .createQuery<ProgramProductTypeConfig>(ProgramProductTypeConfig, 'ProgramProductTypeConfigsByProgram')
      .withParameters({ programId });
    return q.execute();
  }

  createProductTypeConfigsQuery(accountId: string, supplierId: string | null, shouldIncludePricing: boolean, isInventory?: boolean) {
    return SharedQueries.createProductTypeConfigsQuery(this.uow, accountId, supplierId, shouldIncludePricing, isInventory);
  }

  //#endregion

  //#region Images

  getAccountImages(...accountImageIds: string[]) {
    let q = this.uow.createQuery(AccountImage).where({ id: { in: accountImageIds } });
    return q.execute();
  }

  getAccountImagesByAccountId(accountId?: string) {
    let q = this.uow.createQuery(AccountImage).where({
      or: [{ accountId: accountId }, { accountId: null }],
    });
    return q.execute();
  }

  async getBillingAccountAddresses(accountId: string) {
    return this.uow
      .createQuery(AccountAddress)
      .expand(['account'])
      .where({
        accountId: accountId,
        isBillingAddress: true,
        isPersonalAddress: false,
      })
      .execute();
  }

  /** Get product type images, preferring non-thumbnails but falling back to thumbnails if none exist */
  async getProductTypeImages(...productTypeIds: string[]) {
    // ensure unique list
    productTypeIds = [...new Set(productTypeIds)];
    // split into smaller chunks so query string doesn't get too long
    const chunks = _.chunk(productTypeIds, 30);
    // perform queries - get all images (both thumbnails and non-thumbnails)
    const promises = chunks.map((ids) =>
      this.uow
        .createQuery(ProductTypeImage)
        .where({
          productTypeId: { in: ids },
        })
        .orderBy('isPrimary', true)
        .expand('supplierImage')
        .execute(),
    );
    const resultChunks = await Promise.all(promises);
    const allImages = _.flatten(resultChunks);

    // Prefer non-thumbnail images, but use thumbnails as fallback
    const nonThumbnails = allImages.filter((img) => !img.supplierImage?.isThumbnail);
    return nonThumbnails.length > 0 ? nonThumbnails : allImages;
  }

  /** Non-thumbnail images only */
  async getAddonImages(...addonIds: string[]) {
    return this.uow
      .createQuery(AddonImage)
      .where({
        addonId: { in: addonIds },
        'supplierImage.isThumbnail': false,
      })
      .orderBy('isPrimary', true)
      .expand('supplierImage')
      .execute();
  }

  // TODO - implement on server
  // async getPrimaryImage(productTypeId: string) {
  //   const images = await this.getProductTypeImages(productTypeId);
  //   if (images.length == 0) return null;
  //   const image = images.find(x => x.isPrimary) ?? images[0];
  //   return image;
  // }

  //#endregion

  //#region Other Stuff

  async getNarrativeUserGroupDtos(programId: string, treeId: string) {
    const q = this.uow
      .createQuery<NarrativeUserGroupDto>(NarrativeUserGroupDto, 'GetNarrativeUserGroupDtos')
      .withParameters({ programId, treeId });
    return q.execute();
  }

  async getAdjustmentSummary(sourceDetailId: string): Promise<AdjustmentResult[]> {
    return firstValueFrom(
      this.http.get<AdjustmentResult[]>(environment.accountUrl + '/GetAdjustmentSummary', { params: { sourceDetailId } }),
    );
  }

  /**
   * Training Mode: live server check of whether the current PAA is locked.
   * Used by accountCanActivateGuard so a mid-session lock/unlock takes effect on the
   * next Account Management navigation, without requiring the PAA to re-login.
   */
  async getMyTrainingLockState(): Promise<boolean> {
    return firstValueFrom(this.http.get<boolean>(environment.accountUrl + '/MyTrainingLockState'));
  }

  async checkIssuanceCoverage(pricedProductTypeId: string, tagIdToRemove: string): Promise<IssuanceCoverageResult[]> {
    const raw = await firstValueFrom(
      this.http.get<IssuanceCoverageResult[]>(environment.accountUrl + '/CheckIssuanceCoverage', {
        params: { pricedProductTypeId, tagIdToRemove },
      }),
    );
    return raw ?? [];
  }

  /**
   * Lookup-first "Add Account User" — given an email and the current account, returns any
   * existing AccountUsers in this account that already own that email. Empty array means
   * no in-account match, so the caller falls through to the normal create-new flow.
   * Cross-account matches are intentionally not returned (each account's users are
   * independent; AspNetUsers uniqueness still catches identity collisions at save time).
   */
  async findAccountUserForAdd(email: string, accountId: string): Promise<AccountUserMatch[]> {
    if (!email || !email.trim()) return [];
    const raw = await firstValueFrom(
      this.http.get<any[]>(environment.accountUrl + '/FindAccountUserForAdd', {
        params: { email: email.trim(), accountId },
      }),
    );
    return (raw ?? []).map((r) => ({
      accountUserId: r.AccountUserId ?? r.accountUserId,
      proximityUserId: r.ProximityUserId ?? r.proximityUserId,
      fullName: r.FullName ?? r.fullName ?? '',
      username: r.Username ?? r.username ?? '',
      email: r.Email ?? r.email ?? '',
      currentPugIds: r.CurrentPugIds ?? r.currentPugIds ?? [],
    }));
  }

  /**
   * The set of ProgramUserGroup IDs the given AccountAdmin supervises via the Proximity
   * Organization Tree (descendants included). Server-side equivalent of the BeforeSaveHandler
   * scope check, surfaced so the form can filter the PUG-picker and disable detach on
   * out-of-scope PUG rows.
   */
  async getSupervisedPugsForAdmin(accountAdminId: string): Promise<string[]> {
    return (await firstValueFrom(
      this.http.get<string[]>(environment.accountUrl + '/SupervisedPugsForAdmin', {
        params: { accountAdminId },
      }),
    )) ?? [];
  }

  /** True iff the admin can edit this user (at least one of the user's PUGs is supervised). */
  async canEditAccountUser(accountAdminId: string, accountUserId: string): Promise<boolean> {
    return await firstValueFrom(
      this.http.get<boolean>(environment.accountUrl + '/CanEditAccountUser', {
        params: { accountAdminId, accountUserId },
      }),
    );
  }

  /** True iff every PUG the user belongs to is supervised by this admin (R3). */
  async canDeleteAccountUser(accountAdminId: string, accountUserId: string): Promise<boolean> {
    return await firstValueFrom(
      this.http.get<boolean>(environment.accountUrl + '/CanDeleteAccountUser', {
        params: { accountAdminId, accountUserId },
      }),
    );
  }

  /**
   * Per-PUG access counts for the given AccountUser — used to render Full/Partial/No Access
   * on the PUG membership grid. Wire is PascalCase (Account controller uses the same
   * Newtonsoft config as the rest of the API).
   */
  async getPugAccessStatesForAccountUser(accountUserId: string): Promise<{ ProgramUserGroupId: string; ProgramCount: number; ExclusionCount: number }[]> {
    return (await firstValueFrom(
      this.http.get<{ ProgramUserGroupId: string; ProgramCount: number; ExclusionCount: number }[]>(
        environment.accountUrl + '/GetPugAccessStatesForAccountUser',
        { params: { accountUserId } },
      ),
    )) ?? [];
  }

  /** Current-program counts (Active + not-ended) per PUG on this account — used by the
   *  Program User Groups admin page. */
  async getCurrentProgramCountsForPugs(accountId: string): Promise<{ ProgramUserGroupId: string; CurrentProgramCount: number }[]> {
    return (await firstValueFrom(
      this.http.get<{ ProgramUserGroupId: string; CurrentProgramCount: number }[]>(
        environment.accountUrl + '/GetCurrentProgramCountsForPugs',
        { params: { accountId } },
      ),
    )) ?? [];
  }

  /** "Account Users per Program User Group" analysis for the PUG page's Analyze menu.
   *  One row per live (not Inactive) PUG: the live, not-exited Account User count, and the
   *  current Programs the group serves. Server-side aggregate - Breeze cannot group-by count. */
  async getAccountUserCountsPerPug(accountId: string): Promise<PugAccountUserCount[]> {
    return (await firstValueFrom(
      this.http.get<PugAccountUserCount[]>(
        environment.accountUrl + '/GetAccountUserCountsPerPug',
        { params: { accountId } },
      ),
    )) ?? [];
  }

  /** "Account Users by Shipping Address" analysis for the Shipping User Groups page's Analyze
   *  menu. One row per (address, group) pair, so a group with several addresses repeats.
   *  Server-side aggregate - the Analyze dialogs always take a DTO, never Breeze entities. */
  async getAccountUserCountsByShippingAddress(accountId: string): Promise<ShippingAddressAccountUserCount[]> {
    return (await firstValueFrom(
      this.http.get<ShippingAddressAccountUserCount[]>(
        environment.accountUrl + '/GetAccountUserCountsByShippingAddress',
        { params: { accountId } },
      ),
    )) ?? [];
  }

  /** Drill-down for the PUG page's Current Programs column — returns the current
   *  (Active + not-ended) Programs attached to one PUG. */
  async getCurrentProgramsForPug(programUserGroupId: string): Promise<
    { ProgramId: string; ProgramName: string; ProgramTypeName: string; StartDate: string | null; EndDate: string | null; ActiveStatusName: string }[]
  > {
    return (await firstValueFrom(
      this.http.get<
        { ProgramId: string; ProgramName: string; ProgramTypeName: string; StartDate: string | null; EndDate: string | null; ActiveStatusName: string }[]
      >(environment.accountUrl + '/GetCurrentProgramsForPug', { params: { programUserGroupId } }),
    )) ?? [];
  }

  /** Count of unplaced (Setup/AwaitingApproval/AwaitingPlacement/OnHold) orders for a PUG.
   *  Used by the org-tree ATUG-delete guard to block removals that would strand in-flight orders. */
  async getUnplacedOrderCountForPug(programUserGroupId: string): Promise<number> {
    return (await firstValueFrom(
      this.http.get<number>(environment.accountUrl + '/GetUnplacedOrderCountForPug', { params: { programUserGroupId } }),
    )) ?? 0;
  }

  /** How many orders on this Program are collected — in AwaitingRelease, awaiting administrator
   *  release. Collect, Organize and Place cannot be switched off, nor its scope changed, while
   *  this is non-zero: doing so would strand those orders, since the release action only exists
   *  while the Program is collecting. */
  async getCollectedOrderCountForProgram(programId: string): Promise<number> {
    return (
      (await firstValueFrom(this.http.get<number>(environment.accountUrl + '/GetCollectedOrderCountForProgram', { params: { programId } }))) ?? 0
    );
  }

  /** The orders in one collection batch, as Breeze entities.
   *
   *  Entities rather than a DTO because the review page EDITS them — applies a purchase order,
   *  sends one back to approval, notes one — and entities plus saveChanges gives that for free.
   *  It also means order value comes from JobOrder.getTotalAmt(), the proven client calculation,
   *  instead of a third server-side re-derivation of (base + featureUpcharges + addons) x qty.
   *
   *  The expand is wide but bounded by batch size, and every branch of it earns its place:
   *    - the pricing triple feeds getTotalAmt()
   *    - programUserGroupMaps answers "is this user still in the group their order names"
   *    - shippingUserGroupMaps answers "may this user ship to the address on the order"
   *    - accountPurchaseOrderLogs answers "is the purchase order over its limit"
   */
  async getCollectedJobOrders(programId: string, programUserGroupId?: string) {
    const where: Record<string, unknown> = {
      programId: programId,
      jobOrderStatusId: JobOrderStatusEnum.AwaitingRelease,
    };
    if (programUserGroupId) {
      where['programUserGroupId'] = programUserGroupId;
    }
    return await this.uow
      .createQuery(JobOrder)
      .where(where)
      .expand([
        'accountUser.proximityUser',
        'programUserGroup',
        'shippingAccountAddress',
        'accountPurchaseOrder.accountPurchaseOrderLogs',
        // Pricing — the minimum getTotalAmt() needs.
        'jobOrderDetails.jobOrderDetailAddons.pricedAddon',
        'jobOrderDetails.productTypeConfigProduct.productTypeConfig.pricedProductType.pricedProductTypeFeatureChoices',
        'jobOrderDetails.productTypeConfigProduct.product.productFeatureChoices',
        // Gate #3 and gate #4 — both are questions about the USER, not the order.
        'accountUser.programUserGroupMaps',
        'accountUser.shippingUserGroupMaps.shippingUserGroup.shippingUserGroupAddressMaps',
      ])
      .execute();
  }

  /** Collect, Organize and Place rolled up per ACCOUNT, for the Global Administrator's chooser.
   *  Cross-account, so the server clears its query filters for this one call. */
  async getAccountCollectionSummaries(): Promise<AccountCollectionSummaryDTO[]> {
    return (
      (await firstValueFrom(
        this.http.get<AccountCollectionSummaryDTO[]>(environment.accountUrl + '/GetAccountCollectionSummaries'),
      )) ?? []
    );
  }

  /** Every open collection batch on this account, for the Collections list page. Batch headers and
   *  chase-item counts only — per-order defects come from getCollectionReadiness for the one batch
   *  actually being opened, since evaluating them for every batch to render a list is wasteful. */
  async getCollectionBatches(accountId: string): Promise<CollectionBatchDTO[]> {
    return (
      (await firstValueFrom(this.http.get<CollectionBatchDTO[]>(environment.accountUrl + '/GetCollectionBatches', { params: { accountId } }))) ??
      []
    );
  }

  /** One batch, fully evaluated: header, both chase-item lists, and every collected order with its
   *  three per-order gates resolved. programUserGroupId is required under PUG-wide scope and
   *  ignored under Program-wide. Nothing here blocks a release — every gate is a warning. */
  async getCollectionReadiness(programId: string, programUserGroupId?: string): Promise<CollectionReadinessDTO> {
    const params: Record<string, string> = { programId };
    if (programUserGroupId) params['programUserGroupId'] = programUserGroupId;
    return await firstValueFrom(this.http.get<CollectionReadinessDTO>(environment.accountUrl + '/GetCollectionReadiness', { params }));
  }

  /** Chase a collection by hand: remind the people who have not ordered (tellUsers), send the
   *  outstanding list to the administrators who cover the batch (tellAdmins), or both.
   *
   *  Runs the same server path as the scheduled 6:30am nag, so a hand-sent chase and a scheduled
   *  one are the same email to the same computed list. Resolves with how many notifications were
   *  QUEUED - delivery is the notification dispatcher's next 15-minute pass, not this call. */
  async sendCollectionChase(
    programId: string,
    programUserGroupId: string | undefined,
    tellUsers: boolean,
    tellAdmins: boolean,
    accountUserIds?: string[],
  ): Promise<number> {
    const params: Record<string, string> = { programId, tellUsers: String(tellUsers), tellAdmins: String(tellAdmins) };
    if (programUserGroupId) params['programUserGroupId'] = programUserGroupId;
    // Empty or omitted means the whole outstanding list. The server intersects whatever it is given
    // with who is actually outstanding, so a stale page cannot chase somebody who has since ordered.
    const body = accountUserIds && accountUserIds.length > 0 ? accountUserIds : null;
    return await firstValueFrom(this.http.post<number>(environment.accountUrl + '/SendCollectionChase', body, { params }));
  }

  /** Other Programs already accepting new orders through this Organization Tree. Empty means this
   *  Program may activate on it. Active-scoped deliberately: a cycled Program parks on Hold against
   *  the outgoing Program's tree by design, and that is legal. */
  async getOtherActiveProgramsOnTree(approvalTreeId: string, programId: string): Promise<string[]> {
    return (
      (await firstValueFrom(
        this.http.get<string[]>(environment.accountUrl + '/GetOtherActiveProgramsOnTree', { params: { approvalTreeId, programId } }),
      )) ?? []
    );
  }

  /** For each program-specific Organization Tree on this account, the current Program that owns it.
   *  Feeds the Program form's tree picker so a taken tree is shown disabled and annotated rather
   *  than filtered out. Designated trees with no current program produce no row and stay selectable. */
  async getDesignatedTreeOwners(accountId: string): Promise<{ ApprovalTreeId: string; ProgramId: string; ProgramName: string }[]> {
    return (
      (await firstValueFrom(
        this.http.get<{ ApprovalTreeId: string; ProgramId: string; ProgramName: string }[]>(
          environment.accountUrl + '/GetDesignatedTreeOwners',
          { params: { accountId } },
        ),
      )) ?? []
    );
  }

  /** Pre-demotion check — server returns why an Organization Tree cannot be released back to the
   *  shared pool. Empty array means it can. Demotion is always a manual, intentional act; this only
   *  makes sure it is a safe one. */
  async getTreeDemotionBlockers(approvalTreeId: string): Promise<{ Category: string; Count: number; SampleNames: string[] }[]> {
    return (
      (await firstValueFrom(
        this.http.get<{ Category: string; Count: number; SampleNames: string[] }[]>(
          environment.accountUrl + '/GetTreeDemotionBlockers',
          { params: { approvalTreeId } },
        ),
      )) ?? []
    );
  }

  /** Pre-designation check for an Organization Tree — server returns why it cannot be marked
   *  program-specific. Empty array means it can be.
   *
   *  NOT a dead end when non-empty: a program-specific tree may serve only one current program,
   *  but the promote-on-demand flow can copy the tree instead. Present it that way, never as a
   *  refusal. See Docs/Program-Specific-Org-Trees.md §5a. */
  async getTreeDesignationBlockers(approvalTreeId: string): Promise<{ Category: string; Count: number; SampleNames: string[] }[]> {
    return (
      (await firstValueFrom(
        this.http.get<{ Category: string; Count: number; SampleNames: string[] }[]>(
          environment.accountUrl + '/GetTreeDesignationBlockers',
          { params: { approvalTreeId } },
        ),
      )) ?? []
    );
  }

  /** Pre-delete check for a PUG — server walks every FK-holding table and returns a category
   *  breakdown of what's blocking. Empty array means safe to delete. */
  async getPugDeleteBlockers(programUserGroupId: string): Promise<
    { Category: string; Count: number; SampleNames: string[] }[]
  > {
    return (await firstValueFrom(
      this.http.get<{ Category: string; Count: number; SampleNames: string[] }[]>(
        environment.accountUrl + '/GetPugDeleteBlockers',
        { params: { programUserGroupId } },
      ),
    )) ?? [];
  }

  // Pure read-only legality preview for tag consolidation. See ConsolidateProductTypeTags
  // on the server for the rules. Client renders the result; the user only sees a Consolidate
  // button when IsLegal is true.
  async checkTagConsolidationLegality(canonicalTagId: string, synonymTagIds: string[]): Promise<TagConsolidationLegalityDto> {
    const request: TagConsolidationRequestDto = { CanonicalTagId: canonicalTagId, SynonymTagIds: synonymTagIds };
    return await firstValueFrom(
      this.http.post<TagConsolidationLegalityDto>(environment.accountUrl + '/CheckTagConsolidationLegality', request),
    );
  }
  //#endregion

  //#region Supplier API Queries

  async getCancellableDetails(proximityJobOrderIds: string[]): Promise<CancellableDetailDTO[]> {
    if (!proximityJobOrderIds || proximityJobOrderIds.length === 0) return [];
    const params = { proximityJobOrderIds: proximityJobOrderIds.join(',') };
    // The AccountController uses BreezeAsyncQueryFilter which serializes with PascalCase.
    // We receive raw objects with PascalCase keys and must map to camelCase DTO properties.
    const raw = await firstValueFrom(this.http.get<any[]>(environment.accountUrl + '/GetCancellableDetails', { params }));
    return (raw ?? []).map((r) => ({
      proximityJoDetailId: r.ProximityJoDetailId ?? r.proximityJoDetailId,
      joHeaderId: r.JoHeaderId ?? r.joHeaderId,
      orderQty: r.OrderQty ?? r.orderQty,
      cancellableQty: r.CancellableQty ?? r.cancellableQty,
      requiresAdminApproval: r.RequiresAdminApproval ?? r.requiresAdminApproval,
      reason: r.Reason ?? r.reason,
    }));
  }

  /**
   * Fetches all open order details (OpenQty > 0) for the current account, scoped to the
   * current admin's supervised PUGs. Powers the "Open Orders by Program User Group" dashboard
   * tile. Live per-call — the server has no Proximity-side cache. Wire format is PascalCase
   * (raw controller response, not Breeze); we defensively read both cases.
   */
  /**
   * The admin-visible active PUGs for the given account, sorted alphabetically. Feeds the
   * "Open Orders by Program User Group" tile's dropdown so every visible PUG appears in the
   * picker even when it has zero open orders. Wire format is PascalCase.
   */
  async getVisiblePugsForOpenOrdersTile(accountId: string): Promise<PugOptionDTO[]> {
    const raw = await firstValueFrom(
      this.http.get<any[]>(environment.accountUrl + '/GetVisiblePugsForOpenOrdersTile', { params: { accountId } }),
    );
    return (raw ?? []).map((r) => ({
      id: r.Id ?? r.id,
      name: r.Name ?? r.name,
    }));
  }

  async getOpenOrderDetailsByPug(accountId: string): Promise<OpenOrderByPugDTO[]> {
    const raw = await firstValueFrom(
      this.http.get<any[]>(environment.accountUrl + '/GetOpenOrderDetailsByPug', { params: { accountId } }),
    );
    return (raw ?? []).map((r) => ({
      proximityJoHeaderId: r.ProximityJoHeaderId ?? r.proximityJoHeaderId,
      proximityJoDetailId: r.ProximityJoDetailId ?? r.proximityJoDetailId,
      jobOrderDisplayId: r.JobOrderDisplayId ?? r.jobOrderDisplayId,
      orderDate: new Date(r.OrderDate ?? r.orderDate),
      expectedShipDate: (r.ExpectedShipDate ?? r.expectedShipDate) ? new Date(r.ExpectedShipDate ?? r.expectedShipDate) : null,
      expectedShipDateIsOriginal: r.ExpectedShipDateIsOriginal ?? r.expectedShipDateIsOriginal ?? false,
      openQty: r.OpenQty ?? r.openQty,
      programUserGroupId: r.ProgramUserGroupId ?? r.programUserGroupId,
      programUserGroupName: r.ProgramUserGroupName ?? r.programUserGroupName,
      accountUserId: r.AccountUserId ?? r.accountUserId,
      accountUserName: r.AccountUserName ?? r.accountUserName,
      productTypeDescription: r.ProductTypeDescription ?? r.productTypeDescription,
      productConfigName: r.ProductConfigName ?? r.productConfigName,
      features: r.Features ?? r.features,
    }));
  }

  //#endregion
}

/**
 * One PUG-picker entry for the "Open Orders by Program User Group" dashboard tile.
 * Mirrors Proximity.Api.DTO.PugOptionDTO.
 */
/**
 * Collect, Organize and Place — batch readiness wire types.
 *
 * PascalCase, because these are raw controller responses rather than Breeze entities. Breeze
 * camel-cases its own metadata; nothing else on the wire is camelCase in Proximity.
 */

/** One collectible batch: a Program under Program-wide scope, or a (Program x PUG) pair under PUG-wide. */
export interface CollectionBatchDTO {
  ProgramId: string;
  ProgramName: string;
  CollectionScopeId: CollectionScopeEnum;
  /** Null under Program-wide scope, where the batch is the whole Program. */
  ProgramUserGroupId: string | null;
  ProgramUserGroupName: string | null;
  /** Gate #1. Soft — orders keep collecting past it and releasing early only warns. */
  EarliestPlacementDate: string | null;
  IsPlacementDateReached: boolean;
  /** Lapsed and still unreleased. What the nag keys off. */
  IsPastDue: boolean;
  /** Negative once the placement date has passed; null when unset. */
  DaysUntilPlacement: number | null;

  // The scoreboard counts PEOPLE, not orders — "how many of my people have come through".
  // The roster is the denominator and the headline is a fraction. See Docs §9.
  /** Active users on the roster for this batch's scope. The denominator. */
  ExpectedUserCount: number;
  /** Distinct users represented by the orders in the collection. */
  CollectedUserCount: number;
  /** Users with no order at all, or one still in Setup as an unsubmitted draft. */
  OutstandingUserCount: number;

  CollectedOrderCount: number;
  AwaitingApprovalOrderCount: number;
  AwaitingApprovalUserCount: number;
  // No money here — order value comes from JobOrder.getTotalAmt() on the Breeze entities.
}

/** Somebody to chase: what is wrong is ABSENT from the batch rather than fixable inside it. */
export interface CollectionChaseItemDTO {
  AccountUserId: string;
  UserName: string;
  /** Where a reminder would actually go. Empty is worth seeing - that user cannot be chased by
   *  email at all, which is a different problem from not having ordered. */
  UserEmail: string;
  ProgramUserGroupName: string;
  /** Personal shipping address if they have one, else whatever their shipping groups reach. */
  ShippingAddressName: string;
  /** Null when the user has no order at all; set when one exists but has not arrived. */
  JobOrderId: string | null;
  JobOrderDisplayId: string | null;
}

/** What the batch page cannot get from Breeze — all of it questions about ABSENCE. */
export interface CollectionReadinessDTO {
  Batch: CollectionBatchDTO;
  /** Roster users with no order, or one still in Setup. */
  MissingOrders: CollectionChaseItemDTO[];
  /** Orders still with an approver, so not in the batch. */
  StuckInApproval: CollectionChaseItemDTO[];
}

/** One account's Collect, Organize and Place roll-up. Only accounts with an open batch appear. */
export interface AccountCollectionSummaryDTO {
  AccountId: string;
  AccountName: string;
  /** One per collecting Program under Program-wide, one per group under PUG-wide. */
  BatchCount: number;
  CollectedOrderCount: number;
  OutstandingUserCount: number;
  PastDueBatchCount: number;
  EarliestPlacementDate: string | null;
}

export interface PugOptionDTO {
  id: string;
  name: string;
}

/**
 * Client-side shape of one row of the "Open Orders by Program User Group" dashboard tile.
 * Mirrors the server-side Proximity.Api.DTO.OpenOrderByPugDTO with camelCase properties.
 */
export interface OpenOrderByPugDTO {
  proximityJoHeaderId: string;
  proximityJoDetailId: string;
  jobOrderDisplayId: string | null;
  orderDate: Date;
  expectedShipDate: Date | null;
  expectedShipDateIsOriginal: boolean;
  openQty: number;
  programUserGroupId: string | null;
  programUserGroupName: string | null;
  accountUserId: string | null;
  accountUserName: string | null;
  productTypeDescription: string | null;
  productConfigName: string | null;
  features: string | null;
}

/**
 * Client-side shape of one row of the "Account Users per Program User Group" analysis.
 * Mirrors Proximity.Api.DTO.PugAccountUserCountDto. The wire is PascalCase - the API turns
 * camel casing off for Breeze compatibility (Startup.cs AddNewtonsoftJson).
 */
export interface PugAccountUserCount {
  ProgramUserGroupId: string;
  ProgramUserGroupName: string;
  ActiveStatusName: string;
  AccountUserCount: number;
  ProgramCount: number;
  /** The current Program names, sorted and joined with ", ". */
  Programs: string;
}

/**
 * Client-side shape of one row of the "Account Users by Shipping Address" analysis.
 * Mirrors Proximity.Api.DTO.ShippingAddressAccountUserCountDto. PascalCase on the wire.
 */
export interface ShippingAddressAccountUserCount {
  ShippingUserGroupId: string;
  ShippingUserGroupName: string;
  ActiveStatusName: string;
  ShippingAccountAddressId: string | null;
  ShippingAddressName: string;
  ShippingAddressDescription: string;
  AccountUserCount: number;
}
